/**
 * Nursing homes — the control centre (T939 M5).
 *
 * WHAT THIS IS, AND WHY IT IS ONE SCREEN. Alex ruled it on 2026-08-10 after
 * reading the prototype directly: the rail row `Nursing home requests` becomes
 * `Nursing homes`, one destination absorbing BOTH the requests queue that lived
 * on its own screen AND the nurse roster that was a section of "Who can sign
 * in". Nurse management now appears in exactly ONE place in the product (P0-2),
 * and this is it.
 *
 * ⛔ IT IS A REPLACEMENT, NOT AN ADDITION, AND THE RAIL BUDGET DEPENDS ON THAT.
 * SC-10b (e2e/tests/green/tablet-ia.spec.ts) asserts the rail fits ~700px
 * against the ~700px plane (R11), which the rail clears by single-digit pixels in
 * its worst badge state; the `699px` this line carried was measured on the PRE-R6
 * rail and the live figures are in DESIGN.md section 4.3. The rail stays at EIGHT
 * items because
 * this row took the old one's place. If a ninth rail row is ever added, that
 * argument is void — see the note on the button in Rail.tsx.
 *
 * ⭐ VARIANT C — "roster in place". There is NO home picker. Each home block
 * carries its own "Add a nurse here", so the home comes from context and
 * variant A's wrong-row failure CANNOT occur rather than being warned about.
 * Accounts that have no portal home yet get their own named region at the foot,
 * which is what makes the bootstrap case (the FIRST nurse at a home) reachable
 * at all — the case Owen hits first.
 *
 * ⚡⚡ SUPERSEDED 2026-08-13 (T1011 M8): THE SECTIONS ARE NOW TABBED. Alex chose
 * DIRECTION A at the mockup gate on 2026-08-12, which adds a third job — the
 * invoices and what every home owes — to a screen that was already two stacked
 * sections and a table. Three stacked sections is a scroll nobody finishes.
 * ⛔ THE ORIGINAL RULING IS KEPT BELOW RATHER THAN DELETED, because the fork it
 * names is real and its three grounds did not stop being true; two of them are
 * now DISCHARGED BY MECHANISM rather than by argument, and that is the whole
 * difference. Read the ruling, then read what answers it.
 *
 * ⛔ THE ORIGINAL RULING (T939 M5): THE SECTIONS ARE STACKED, NOT TABBED, AND
 * THAT WAS THE FORK. A sub-view switcher was the other candidate. Stacking won on
 * three measured grounds:
 *   - DAY ONE IS THE BOOTSTRAP STATE. Production holds three portal homes and
 *     ZERO accounts, and the account seed is hand-run — so on first open the
 *     requests queue is empty and the only actionable thing on the screen is
 *     the "Not in the portal yet" region. Behind a non-default tab, that is
 *     invisible on the one day it is the whole job.
 *   - A TAB CREATES A MODE ERROR. The rail badge counts WAITING REQUESTS. With
 *     the roster showing, the badge can tick while the thing it counts is not
 *     on screen; a scroll fixes that, a mode does not.
 *   - THE IDIOM DOES NOT EXIST HERE. There is no `role="tab"` and no
 *     `aria-selected` anywhere in this client, so a switcher would be a new
 *     navigation concept to learn on the screen whose brief is "Helen bounced
 *     off a tool with too many buttons".
 * Stacking is also the reversible direction: adding a switcher later is
 * additive, removing one Helen has already learned is not — and this milestone is
 * that additive change, taken deliberately, not a violation.
 *
 * ⭐ HOW THE THREE GROUNDS ARE ANSWERED, EACH BY A MECHANISM AND NOT BY A PROMISE:
 *   1. BOOTSTRAP. The DEFAULT TAB is decided by the waiting count, not by
 *      position: with nothing waiting the screen opens on `Homes`, which is the
 *      tab carrying "Not in the portal yet". Production's day-one state — three
 *      portal homes, ZERO accounts, an empty queue — therefore opens on exactly
 *      the region the ruling said a tab would hide. Driven in that state by test.
 *   2. MODE ERROR. The `Requests` TAB BUTTON CARRIES THE WAITING COUNT, read from
 *      the SAME value the rail badge renders (App threads `waitingRequests` from
 *      the store both places). So the badge cannot tick while the thing it counts
 *      is off-screen without the tab button saying so — by construction, from one
 *      source, rather than by two counters that could drift.
 *   3. THE IDIOM. Still true, and still respected: this is an `aria-pressed`
 *      BUTTON GROUP copied from `AccountBook.tsx:190-202`, the switcher idiom this
 *      client already has — ⛔ NOT `role="tab"`/`aria-selected`, which appears
 *      NOWHERE in this client and would be a genuinely new concept.
 * ⛔ AND THERE ARE FOUR TABS, NOT THREE AND NOT FIVE (T1746 M4, q747: Alex,
 * 2026-09-04). `Bookings` was added because the crossing now MAKES bookings on
 * this destination and there was nowhere on it to see them; it is never the
 * default tab and it fetches nothing until it is chosen. ⛔ The approved M8
 * mockup drew a `Prices` tab and q747 kept it off: that tab set belonged to
 * `AccountBook.tsx`. ⭐⭐ REVERSED AT board 2812 M4 (R-2812-e; DESIGN.md §4.2): the
 * screen is a COLUMN OF HOMES beside the page, and All homes and a home both show To
 * do, Bookings, Prices and Invoices (a home's prices on its own page, `HomePage.tsx`);
 * the old `Homes` tab (the roster and the account book) is "Set up homes and places"
 * at the column's foot, and a home's settings are behind its Details button.
 *
 * ⚠️ ORDER IS DAILY-FIRST. Requests land daily; adding a nurse is rare. The
 * queue is therefore first and carries the rail badge's meaning; the roster
 * sits under it.
 *
 * ⚠️ THIS SCREEN IS DATA-SCALED, so it is deliberately absent from
 * test/client/control-budget.spec.tsx's DEBT enumeration: its control count is
 * a function of how many homes, nurses and unlinked accounts exist, and a DEBT
 * row must record an EXACT number. DESIGN.md §1 rule 2 (<= 7 controls) is
 * over-run here exactly as it already is on the requests table this absorbs —
 * recorded rather than hidden, and the per-home add form is REVEALED rather
 * than always-open specifically to keep the resting count down.
 */
import type { ComponentChildren, VNode } from 'preact';
import { useEffect, useRef, useState } from 'preact/hooks';
import {
  INBOX_POLL_MS,
  createPortalHome,
  createPortalNurse,
  linkPortalHome,
  savePortalHomeSettings,
  listAccounts,
  listHomeGroups,
  listPortalAccessReview,
  listPortalHomes,
  listPortalNurseEvents,
  setPortalNurseActive,
  type ApiAccount,
  type ApiHomeGroup,
  type PortalAccessNurse,
  type PortalAccessReview,
  type PortalHome,
  type PortalNurseEvent,
  type PortalNurseRole,
} from '../../lib/actions.js';
import { shortDateTime } from '../../lib/format.js';
import type { Registry } from '../../store/registry.js';
import { Button, EmptyState, FormField, SubRail, TabStrip, type SubRailItem } from '@ecmb/ui';
import { ApiError } from '../../lib/api.js';
import { Banner } from '../../components/Banner.js';
// ⭐⭐ T1749 M7 (R5) — THE CUSTOMER ACCOUNT BOOK LIVES UNDER THIS SCREEN'S HOMES
// TAB NOW. `AccountBook` renders its own nested strip and its three lists; the
// loader is the one both hosts share, so the degrade rules are written once.
import { AccountBook } from './AccountBook.js';
import {
  loadAccountBook,
  EMPTY_ACCOUNT_BOOK,
  type AccountBookData,
} from '../../lib/account-book-loader.js';
import { PortalRequests } from './PortalRequests.js';
import { TodoPiles } from '../todo/TodoPiles.js';
import { AccountInvoices } from './AccountInvoices.js';
import { PhoneInTrip } from './PhoneInTrip.js';
import { BookingsList, FindField, type BookingsLanding, type BookingsWhen } from '../Bookings.js';
import {
  HOME_PAGE_WORDS, HomeDetails, HomeHeading, HomePrices, HomesPriceCounts, accountForKey, homeSubtitle,
} from './HomePage.js';

/**
 * The portal's cap on `portal.homes.fallback_phone`, in CHARACTERS.
 *
 * ⛔ COPIED, NOT IMPORTED, for the reason `portal-ops-client.ts` records about
 * `PORTAL_DESTINATION_PROJECTION_MAX`: `quote-helper/Dockerfile` COPYs
 * `quote-helper/`, `packages/ui/` and `docs/pricing.json` — `portal/` is not in
 * the image, so an import across that boundary typechecks here, runs locally
 * where the whole tree is present, and throws ERR_MODULE_NOT_FOUND in the
 * container. ⚠️ AND THIS IS THE CLIENT BUNDLE, which reaches `src/core/` no more
 * than it reaches `portal/` — no screen in this directory imports from `core/`.
 * ⇒ the drift is closed where it CAN be: a TEST crosses the boundary (tests do
 * not ship) and pins the two numbers equal — `test/client/nursing-homes.spec.tsx`,
 * "the portal's own fallback-phone cap". If the portal moves its number, that
 * pin reds and this line moves with it. That is the mechanism, not a nuisance.
 *
 * ⛔ CHARACTERS, NOT UTF-16 UNITS, wherever it is compared. The column CHECK is
 * Postgres `length()`, which counts characters, and the route measures with
 * `Array.from`. A `.length` comparison here would admit a value the database
 * then refuses as a raw 500 — the exact defect the route's own comment names.
 */
const FALLBACK_PHONE_MAX = 200;

export type NursingHomesProps = {
  /**
   * ⭐⭐ T1749 M7 (R5) — WHICH TAB TO OPEN ON, when another screen asked. The
   * shelf's `Customer accounts & prices` entry is a link-through to the account
   * book, which lives under `homes`, so the request arrives as a tab rather than
   * as a ScreenId of its own (R5: the cover becomes a link-through).
   * ⛔ IT OVERRIDES THE BOOTSTRAP ANSWER AND NOTHING ELSE. `undefined` on every
   * ordinary visit, which leaves the waiting-requests heuristic below exactly as
   * it was; a value only ever decides the OPENING tab, never a later one.
   */
  /** ⭐ board 2812 M4: or `setup`, Set up homes and places (the shelf's link-through to
   *  the account book, which is no longer a tab). */
  openOn?: TabId | 'setup';
  /** ⭐ board 2812 M3: the request whose card opens first on To do (Tomorrow's
   *  waiting row in Bookings opens its card here). Read once, at mount. */
  openRequestId?: number;
  /**
   * ⭐⭐ board 2812 M4 (plan step 2) — WHICH HOME'S PAGE IS SHOWING, by its group key
   * (Q240), or null for All homes: the store's `viewHomeKey`, so it survives the trip to
   * a booking and Back returns to the home. `onOpenHome` sets it (never a history
   * step). Both absent on a bare mount (a spec): the screen then holds its own.
   */
  viewHomeKey?: string | null;
  onOpenHome?: (key: string | null) => void;
  /**
   * ⭐ board 2812 fix round (Captain's /check item 8) — WHETHER THE HOME'S DETAILS IS
   * OPEN: the store's `viewHomeDetails`, so the global Back closes Details and stays on
   * the home (`goBack`) rather than leaving the area. `onHomeDetails` sets it. Both
   * absent on a bare mount (a spec): the screen then holds its own, as `viewHomeKey`.
   */
  homeDetailsOpen?: boolean;
  onHomeDetails?: (open: boolean) => void;
  /** ⭐ T1749 M7 wave-3: WHICH SECTION of the Homes tab, when the caller asked
   *  for one. The shelf's link-through wants `accounts`; an ordinary visit wants
   *  the roster, which is `homes`. */
  openSection?: SectionId;
  /** Called once the override has been consumed, so a second visit to this screen
   *  opens on the ordinary bootstrap answer rather than on a stale request. */
  onOpened?: () => void;
  /** ⛔ The requests queue's write-side badge refresh, threaded straight
   *  through. It is REQUIRED rather than optional for the reason PortalRequests
   *  states: the rail's count changes on this screen, and the 30s poll alone
   *  leaves the badge contradicting the row Helen just acted on. */
  onRequestsChanged: () => void;
  /**
   * ⭐ HOW MANY REQUESTS ARE WAITING — the SAME number the rail badge renders,
   * threaded from the store rather than counted again here (T1011 M8).
   *
   * ⛔ IT IS WHAT DISCHARGES THE TABBING RULING'S SECOND GROUND, so it is
   * REQUIRED rather than optional. The badge counts waiting requests and could
   * otherwise tick while its tab is unselected with nothing on screen saying so;
   * the `Requests` tab button renders this value, from the same source, so the
   * two cannot disagree. A SECOND read here would reintroduce exactly the drift
   * it exists to prevent.
   *
   * `null` means the badge's own read FAILED — a different fact from zero, and
   * the tab says so rather than claiming nothing is waiting.
   */
  waitingRequests: number | null;
  /** ⭐ Board 2814 M8R1 (R3): App's portal-count poll tick, threaded straight through
   *  so the Requests rows reload on the poll that moves the count. Optional: a mount
   *  with no poll (a spec, the catalog) reloads on mount only, as before. */
  portalPollTick?: number;
  /**
   * ⭐ T1746 M1 (B4, Q108 option b) - threaded straight through to
   * `PortalRequests`, which renders the booking a crossed request made. REQUIRED
   * for the reason that screen's own prop states: the control claims to open a
   * booking, so a mount that forgot to wire it would render a lie.
   * ⭐ board 2812 M3 fix round (Captain's item 5): `from` names the tab the booking
   * was opened on, so Back from that booking returns to it (App holds the hand-off).
   */
  onOpenBooking: (bookingId: number, from?: NursingTab, at?: BookingsLanding) => void;
  /** ⭐ board 2812 M5 (fix round 1, W2): the Bookings tab's When when its booking was
   *  opened, so Back lands the tab on it. Read once, at mount, like `openOn`. */
  bookingsWhen?: BookingsWhen;
  /**
   * ⛔ THE REGISTRY IS HERE ONLY TO BE PASSED ON, and it is passed rather than
   * re-read for the same reason `waitingRequests` is: `bookingStatusLabel` is
   * the one place a `bookings.status` becomes an operator word, and the crossed
   * request row renders that word. A second vocabulary on this screen would
   * drift from the booking it names.
   */
  registry: Registry;
};

/**
 * The four jobs this destination carries, on All homes and on a home's page alike.
 * ⚠️ ORDER IS DAILY-FIRST AND UNCHANGED: requests land daily and stay first;
 * `Bookings` sits directly under them because it is what a request BECOMES.
 */
/* ⭐ board 2812 M3 (plan step 5): the first tab is `todo`, read "To do", and hosts
   the channel's `TodoPiles` over 2814's Needs you now; the other three are as they
   were until M4. */
/* ⭐⭐ board 2812 M4 (R-2812-e, plan step 3): All homes and a home both show exactly To
   do, Bookings, Prices and Invoices. `homes` left the strip (its content is Set up homes
   and places, at the column's foot) and `prices` joined it: q747 reversed, a Prices tab
   on each home (DESIGN.md §4.2). The order is `TabStripView`'s closed set. */
const TAB_ORDER = ['todo', 'bookings', 'prices', 'invoices'] as const;
type TabId = (typeof TAB_ORDER)[number];
/** The tab names App may hand back as `openOn` (Back to the tab a booking was opened on). */
export type NursingTab = TabId;

/**
 * ⭐⭐ T1749 M7 wave-3 (R5) — THE HOMES TAB'S OWN SECTIONS, and the roster is the
 * FIRST of them rather than the thing the book hangs off. Four segments, one
 * visible at a time, on a strip at the top of the pane.
 */
/**
 * The confirm-in-place limb for a destructive row action, WITH THE KEYBOARD
 * FOLLOWING IT (T1749 M7 rework 1).
 *
 * ⛔ WHY IT EXISTS AT ALL: `Remove access` was ONE TAP straight into an
 * irreversible write. §5.1 asks a destructive for confirm plus undo, and the
 * write is journalled and append-only, so there is nothing to undo afterwards —
 * which makes the confirm the only place the operator can change her mind. This
 * is the same shape `PortalRequests.tsx`'s `ConfirmInPlace` already carries; it
 * is duplicated rather than imported because that one is local to its screen and
 * a screen may not import another screen (the catalog's import-graph arm records
 * the same rule for the same reason). The day a third screen needs it, it is a
 * shared component.
 * ⛔ FOCUS BOTH WAYS. The limb REPLACES the control that armed it, so the element
 * the keyboard was on leaves the DOM and `activeElement` falls back to `<body>`;
 * on dismissal the same happens in reverse. On mount it takes the limb's first
 * control; on unmount it hands the keyboard back to whatever now occupies the
 * limb's position, by POSITION rather than by label.
 */
function ConfirmInPlace({ children }: { children: ComponentChildren }) {
  const limb = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const node = limb.current;
    if (node === null) return undefined;
    const host = node.parentElement;
    const at = host === null ? -1 : [...host.children].indexOf(node);
    node.querySelector('button')?.focus();
    return () => {
      // ⛔ A MICROTASK: this cleanup runs DURING the diff that removes the limb,
      // so the control that replaces it may not be in the document yet.
      queueMicrotask(() => {
        if (host === null || at < 0 || !host.isConnected) return;
        for (const child of [...host.children].slice(at)) {
          const back = child instanceof HTMLButtonElement ? child : child.querySelector('button');
          if (back instanceof HTMLElement) { back.focus(); return; }
        }
      });
    };
  }, []);
  return (
    <div class="op-row-confirm" ref={limb}>
      {children}
    </div>
  );
}

/* ⭐ board 2812 M4 fix round (L8, plan-alignment L-2): Agreed prices LEAVES Set up homes
   and places. A home's prices live on its own Prices tab and nowhere else (q747's
   reversal, one place); Set up holds the roster, Accounts and Destinations. */
const SECTION_ORDER = ['homes', 'accounts', 'destinations'] as const;
type SectionId = (typeof SECTION_ORDER)[number];

const SECTION_LABELS: Record<SectionId, string> = {
  homes: 'Homes',
  accounts: 'Accounts',
  destinations: 'Destinations',
};

/** The account book's own two, which the strip above absorbs. */
const BOOK_SECTIONS: Readonly<Record<string, 'accounts' | 'destinations'>> = {
  accounts: 'accounts', destinations: 'destinations',
};

const TAB_LABELS: Record<TabId, string> = {
  todo: 'To do',
  bookings: 'Bookings',
  prices: 'Prices',
  invoices: 'Invoices',
};

/**
 * The roster's two ESSENTIAL reads, together.
 *
 * ⛔ THE REVIEW IS ESSENTIAL, NOT DECORATIVE, and collapsing it into a
 * null-tolerant read would be a correctness bug rather than a degradation. The
 * home list alone renders a block per home with no nurses under it — which this
 * screen spells "Nobody is set up at this home yet". That sentence is a
 * FALSEHOOD when the truth is "the nurse list could not be read", on the one
 * screen whose job is to say who has access. So both reads gate the section
 * together, and a failure says so.
 */
type RosterState =
  | { kind: 'loading' }
  | { kind: 'error' }
  | { kind: 'ready'; homes: PortalHome[]; review: PortalAccessReview };

/** What each recorded access event reads as, in plain words. An unknown verb
 *  falls back to itself rather than being hidden — the same rule the operator
 *  activity list uses, and for the same reason: a row this build does not
 *  understand still has to be visible. */
function eventPhrase(kind: string): string {
  switch (kind) {
    case 'added':
      return 'added';
    case 'deactivated':
      return 'removed access for';
    case 'reactivated':
      return 'restored access for';
    default:
      return kind;
  }
}

/**
 * ⭐ board 2812 M3 fix round (SC-1) - THE DAY-ONE BOOTSTRAP, AS ONE PREDICATE, and it is
 * T1011 M8's ground exactly: the accounts read WORKED and found NONE, and nothing is
 * waiting (with requests waiting the daily job stays first, as it always has); nobody
 * asked for a tab and she has not picked one. Then the screen opens on Homes ("Not in
 * the portal yet"); on every other visit it stays on To do.
 */
export function zeroAccountsGround(
  accountList: { rows: readonly unknown[] } | null,
  waitingRequests: number | null,
  openOn: TabId | 'setup' | undefined,
  chosen: boolean,
): boolean {
  return accountList !== null && accountList.rows.length === 0
    && !(waitingRequests !== null && waitingRequests > 0)
    && openOn === undefined && !chosen;
}

export function NursingHomes({
  onRequestsChanged,
  waitingRequests,
  portalPollTick,
  onOpenBooking,
  registry,
  openOn,
  openRequestId,
  openSection,
  onOpened,
  viewHomeKey,
  onOpenHome,
  homeDetailsOpen,
  onHomeDetails,
  bookingsWhen,
}: NursingHomesProps) {
  /** ⭐ board 2812 M5 (fix round 1, W2): read once at mount, and spent by the first
   *  Bookings tab that mounts with it (`onLanded`), so a later home switch or tab press
   *  opens on the ordinary When. */
  const [bookingsWhenAtEntry, setBookingsWhenAtEntry] = useState<BookingsWhen | undefined>(bookingsWhen);
  /**
   * ⭐ THE BOOTSTRAP ANSWER, AND IT IS COMPUTED ONCE AT MOUNT — never re-derived
   * on every render.
   *
   * With nothing waiting the screen opens on `Homes`, which is the tab carrying
   * the "Not in the portal yet" region. That is production's day-one state (three
   * portal homes, ZERO accounts, an empty queue), and it is exactly the case the
   * original anti-tab ruling said a tab would hide.
   *
   * ⛔ THE LAZY INITIALISER IS LOAD-BEARING. A value recomputed each render would
   * yank Helen out of the roster the instant a request landed mid-edit — a screen
   * changing mode under her hands, which is a worse mode error than the one the
   * ruling warned about. It decides the OPENING tab and nothing after it.
   */
  const [tab, setTab] = useState<TabId>(() =>
    // ⭐ T1749 M7 (R5): an explicit request wins the OPENING tab. Inside the lazy
    // initialiser, so it is read once at mount exactly as the heuristic is.
    // ⭐⭐ board 2812 M3 fix round (SC-1, the driver's decision): the screen opens on
    // To do ALWAYS, whatever is waiting. The ONE exception is the day-one bootstrap
    // (ZERO accounts, nothing waiting), which keeps T1011 M8's ground and opens on Homes,
    // where "Not in the portal yet" is: the accounts read decides it below (`zeroAccountsGround`),
    // once, and only while nobody has picked a tab. That ground does not apply once an
    // account exists, so "nothing waiting -> Homes" is superseded by SC-1.
    openOn === undefined || openOn === 'setup' ? 'todo' : openOn);
  /**
   * ⭐⭐ board 2812 M4 — SET UP HOMES AND PLACES IS OPEN: today's Homes tab content (the
   * roster and the account book), reached from the column's foot and from the shelf's
   * link-through. The day-one bootstrap (`zeroAccountsGround`) opens it too, so "Not in
   * the portal yet" is still the first thing on the screen on that day (T1011 M8).
   */
  const [setupOpen, setSetupOpen] = useState(openOn === 'setup');
  /** ⭐ board 2812 M4: a home's Details is open (in place of its tab's list).
   *  ⭐ board 2812 fix round (item 8): the store's when App hands it down, so Back
   *  closes it; the screen's own only on a bare mount. */
  const [ownDetails, setOwnDetails] = useState(false);
  const detailsOpen = onHomeDetails !== undefined ? homeDetailsOpen === true : ownDetails;
  const setDetailsOpen = (open: boolean) => {
    if (onHomeDetails !== undefined) onHomeDetails(open);
    else setOwnDetails(open);
  };
  /** ⭐ board 2812 M4: the column's homes, from `/api/home-groups`. */
  const [groups, setGroups] = useState<
    | { kind: 'loading' }
    | { kind: 'error' }
    | { kind: 'ready'; groups: ApiHomeGroup[]; notLinked: number | null }
  >({ kind: 'loading' });
  /** "Find a home" (past eight homes): filters the LOADED homes in the browser, so no
   *  request carries what is typed (Captain's SR1 ruling). */
  const [homeFind, setHomeFind] = useState('');
  /** ⭐ board 2812 M4 fix round (B1): the Bookings tab's Find term is NOT held here: it
   *  lives in `HomesBookingsTab`, beside its box, so the two cannot disagree. */
  /** ⭐ board 2812 M4 fix round (C1): a Details save is re-reading the homes, so the open
   *  home's key may be about to change (`a<id>` to its new short code): the stale-key
   *  fallback waits for that answer (`to` null) and then for the new key to land (`to`
   *  the key) instead of bouncing Helen to All homes. */
  const rekeying = useRef<{ to: string | null } | null>(null);
  /** ⭐ board 2812 M4 fix round (C2): the last name the column's read gave each key, so a
   *  home's page keeps its title when a later read fails. ⭐ board 2812 M5 step 0 (h):
   *  written where a groups answer is APPLIED (`applyGroups`), never during render. */
  const knownNames = useRef(new Map<string, string>());
  /**
   * ⭐⭐ board 2812 M5 step 0 (g) — A GROUPS ANSWER LANDS ONLY IF IT IS STILL THE NEWEST.
   * Every groups read takes a ticket when it STARTS (`groupsAsked`), and its answer is
   * applied only if no read that started after it has already been applied
   * (`groupsShown`). Without it, a poll that left BEFORE a Details save, carrying the
   * home's OLD key, could land AFTER the save's own re-read had moved Helen to the new
   * key, and the stale-key fallback then bounced her to All homes (M4's output gate).
   * Inbox's `latest` guard is the same idea; this one never drops the newest answer.
   */
  const groupsAsked = useRef(0);
  const groupsShown = useRef(0);
  /** ⭐ board 2812 M5 step 0 (g): bumped by every home Helen picks (or a fallback opens),
   *  so a Details save's re-read that lands after she has moved on re-keys nothing. */
  const picks = useRef(0);
  /** ⭐ board 2812 M4 fix round (drive finding D1): the waiting request a Bookings tab's
   *  Tomorrow row asked to open on To do; consumed by the To do it mounts, cleared by the
   *  next tab or home press. */
  const [openWaiting, setOpenWaiting] = useState<number | undefined>(undefined);
  /** The home showing on a bare mount (no store behind it). */
  const [ownKey, setOwnKey] = useState<string | null>(viewHomeKey ?? null);
  const currentKey = onOpenHome !== undefined ? (viewHomeKey ?? null) : ownKey;
  /** Set by her first tab press; the bootstrap answer never moves a tab she chose. */
  const tabChosen = useRef(false);
  const bootstrapRead = useRef(false);
  const [state, setState] = useState<RosterState>({ kind: 'loading' });
  /** Secondary reads. `null` = "could not read it", which each renders as its
   *  own honest line rather than as an empty result. */
  const [accounts, setAccounts] = useState<ApiAccount[] | null | undefined>(undefined);
  const [events, setEvents] = useState<PortalNurseEvent[] | null | undefined>(undefined);
  const [notice, setNotice] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null);
  const [busy, setBusy] = useState(false);
  /** Which home's add form is open, by `home_id`. One at a time: the resting
   *  screen is a list to scan, not N forms to skip past. */
  const [addingAt, setAddingAt] = useState<number | null>(null);
  /** ⭐ T1749 M7 rework 1 — which nurse's REMOVAL is armed, by nurseId. `null` is
   *  "nothing armed"; only a removal arms, never a restore (putting access back
   *  is not destructive and has nothing to undo). */
  const [removingNurse, setRemovingNurse] = useState<number | null>(null);
  const [nurseName, setNurseName] = useState('');
  const [nurseEmail, setNurseEmail] = useState('');
  /** ⭐ T2075 M2 (plan D6) — the sign-in type Helen picks for the new account
   *  (`group` by default: the value every account had before v13) and, for a
   *  group sign-in, the group's name. Both reset with the name and email. */
  const [nurseRole, setNurseRole] = useState<PortalNurseRole>('group');
  const [nurseGroup, setNurseGroup] = useState('');
  /** Which account each home's link picker is pointing at, by `home_id`. Keyed
   *  by a NUMBER, so no prototype-key hazard arises here. */
  const [linkChoice, setLinkChoice] = useState<Record<number, string>>({});
  /** T1868 M4: per-HOME draft of the two settings, keyed by home_id. Held as
   *  STRINGS because they come from inputs — the offset is parsed once, at the
   *  save, so a half-typed value never becomes a number mid-keystroke. */
  const [offsetChoice, setOffsetChoice] = useState<Record<number, string>>({});
  const [phoneChoice, setPhoneChoice] = useState<Record<number, string>>({});
  /** T1146 M2: per-ACCOUNT choice of an existing home to attach it to, keyed by
   *  accountId. Distinct from `linkChoice`, which is per-HOME and keyed by
   *  home_id — the two run in opposite directions and must not share a map. */
  const [attachChoice, setAttachChoice] = useState<Record<number, string>>({});
  /**
   * ⭐⭐ T1749 M7 wave-3 (R5, the driver's R12 decision on impeccable P2-6) —
   * WHICH SECTION OF THE HOMES TAB IS SHOWING.
   *
   * R5 says the Homes tab IS the account book, homes included. M7's first cut
   * APPENDED the book under the roster, and the measurement is why that is not
   * good enough: the roster ran to y ~4470, the book's heading to y 4741 and its
   * only primary to y 7045, so the shelf's link-through landed on a screen whose
   * accounts were ten viewports down with nothing at the top saying they were
   * there. §8: scroll is for reading a list, never for finding the primary.
   * ⇒ ONE SECTION STRIP AT THE TOP OF THE PANE, one section at a time, with the
   * roster as its first segment. The link-through asks for `accounts` and lands
   * on a visible strip.
   */
  const [section, setSection] = useState<SectionId>(openSection ?? 'homes');
  /**
   * ⭐⭐ T1749 M7 (R5) — THE ACCOUNT BOOK'S OWN SNAPSHOT, held apart from the
   * roster's state on purpose: the book must render when the roster read fails
   * (a dead portal must not hide the accounts, destinations and agreed prices
   * that live in THIS database), and the roster must render when the book's four
   * reads fail. Two states, two degrades, neither gating the other.
   * ⚠️ THIS DOES RE-READ `GET /api/accounts`, which the roster array below also
   * reads for its attach control. The duplicate is deliberate and bounded: the
   * roster's array is the subject of this file's POSITIONAL-BIND PIN, and folding
   * its accounts read into the book's snapshot would change the very array that
   * guard watches. simplified: one shared accounts read, upgrade trigger: a
   * milestone that re-sites the positional pin, or a measured cost on screen
   * entry.
   */
  const [book, setBook] = useState<AccountBookData>(EMPTY_ACCOUNT_BOOK);
  const noticeRef = useRef<HTMLDivElement>(null);
  /** The double-submit latch, and it CANNOT be `busy` — `if (busy) return` reads
   *  a value fixed at RENDER time and `setBusy(true)` does not re-render before
   *  the next tap in the same task, so two fast taps both saw `false`. A ref is
   *  written and read synchronously. `busy` stays: it greys the controls out,
   *  which is a different job. Same measurement, same fix as Operators.tsx. */
  const inFlight = useRef(false);

  /**
   * ⛔ THE POSITIONAL-BIND PIN LIVES HERE NOW, and it moved deliberately rather
   * than lapsing.
   *
   * `Operators.tsx` used to carry it on a two-read `Promise.all`, because
   * `Admin.tsx:183-190` destructures SEVEN look-alike `{id,name,active}` reads
   * by position and its own comment warns that appending a fetch in one
   * milestone and another in a second silently binds the WRONG name and still
   * compiles. M5 collapses Operators' array to a single read, which would have
   * retired that pin with nothing behind it — so the guarded array is THIS one,
   * where the four reads are genuinely distinguishable only by position.
   *
   * The spec swaps positions as a mutation, and the fixtures are distinguishable
   * on every field for exactly that reason: two reads that both resolve to
   * objects are invisible to tsc at the destructure.
   *
   * ⚠️ The two SECONDARY reads are caught to `null` rather than allowed to
   * reject, so a dead accounts route or an unreadable audit trail cannot take
   * the roster down with it. The two ESSENTIAL reads are not — see RosterState.
   */
  const load = () => {
    // ⭐ T1749 M7 (R5) — BESIDE THE ROSTER'S ARRAY, never inside it: the book's
    // four reads degrade to a snapshot of their own, and the loader never
    // rejects, so a failing book cannot reach the roster's error arm.
    void loadAccountBook().then(setBook);
    void Promise.all([
      listPortalHomes(),
      listPortalAccessReview(),
      listAccounts().catch(() => null),
      listPortalNurseEvents().catch(() => null),
    ]).then(
      ([homes, review, accountList, eventList]) => {
        setState({ kind: 'ready', homes, review });
        setAccounts(accountList === null ? null : accountList.rows);
        setEvents(eventList);
        // ⭐ board 2812 M3 fix round (SC-1): the day-one bootstrap (ZERO accounts, a
        // read that WORKED: `null` is "could not read", never zero) opens on Homes.
        // Decided ONCE, on the first read: a later reload never moves the tab.
        if (!bootstrapRead.current) {
          bootstrapRead.current = true;
          if (zeroAccountsGround(accountList, waitingRequests, openOn, tabChosen.current)) setSetupOpen(true);
        }
      },
      () => {
        setState({ kind: 'error' });
        // ⛔ THE SECONDARY READS MUST BE RESOLVED HERE TOO. They start
        // `undefined`, which renders as "Getting the recent changes…". A
        // rejection short-circuits the whole `Promise.all`, so leaving them
        // undefined left that line spinning forever under a roster error —
        // a screen that says it is still loading something it has stopped
        // waiting for.
        setAccounts(null);
        setEvents(null);
      },
    );
  };
  useEffect(load, []);

  /** ⭐ board 2812 M5 step 0 (g, h): apply one groups answer, if it is still the newest
   *  (see `groupsAsked`), and remember each key's name (C2). Answers whether it applied. */
  const applyGroups = (ticket: number, g: Awaited<ReturnType<typeof listHomeGroups>>): boolean => {
    if (ticket < groupsShown.current) return false;
    groupsShown.current = ticket;
    const fresh = Array.isArray(g.groups) ? g.groups : [];
    for (const x of fresh) knownNames.current.set(x.key, x.name);
    setGroups({ kind: 'ready', groups: fresh, notLinked: g.notLinked?.needYouNow ?? null });
    return true;
  };
  /** ⭐ board 2812 M4: the column's homes and their counts. A failed read keeps the last
   *  good list (a portal outage is the SERVER's `needYouNow: null`, drawn "!"). */
  const loadGroups = () => {
    const ticket = ++groupsAsked.current;
    return listHomeGroups().then(
      (g) => { applyGroups(ticket, g); },
      (e: unknown) => {
        setGroups((prev) => (prev.kind === 'ready' ? prev : { kind: 'error' }));
        throw e;
      },
    );
  };
  useEffect(() => {
    loadGroups().catch(() => undefined);
    const id = setInterval(() => {
      loadGroups().catch((e: unknown) => {
        if (e instanceof ApiError && (e.status === 401 || e.status === 429)) clearInterval(id);
      });
    }, INBOX_POLL_MS);
    return () => clearInterval(id);
  }, []);

  /** Show a home's page (its group key) or All homes. Not a history step.
   *  ⭐ board 2812 M5 (fix round 1, W6): a notice says what happened on the page it was
   *  raised on ("Saved the settings for …"), so a home switch, a tab press or Details
   *  shut or open clears it. */
  const openHome = (key: string | null) => {
    picks.current += 1;
    setNotice(null);
    setSetupOpen(false);
    setDetailsOpen(false);
    setOpenWaiting(undefined);
    rekeyHome(key);
  };
  /** The same home under another key (C1): the tab and Details stay as they are. */
  const rekeyHome = (key: string | null) => {
    if (onOpenHome !== undefined) onOpenHome(key);
    else setOwnKey(key);
  };
  const openSetUp = (at: SectionId) => {
    setSection(at);
    setSetupOpen(true);
    setDetailsOpen(false);
  };
  const reloadBook = () => void loadAccountBook().then(setBook);
  /** A write on the To do moves the badge AND the column's counts. */
  const onChanged = () => {
    onRequestsChanged();
    loadGroups().catch(() => undefined);
  };

  /* ⭐ board 2812 M4 (plan step 2): A STALE KEY FALLS BACK TO ALL HOMES. A home the
     server no longer lists (renamed code, an account out of use) is not a page. */
  useEffect(() => {
    const re = rekeying.current;
    if (re !== null) {
      // A Details save is re-keying the home: wait for its answer, then for its key.
      if (re.to === null || currentKey !== re.to) return;
      rekeying.current = null;
    }
    if (currentKey !== null && groups.kind === 'ready' && !groups.groups.some((g) => g.key === currentKey)) openHome(null);
  }, [currentKey, groups]);

  /**
   * ⭐⭐ board 2812 M4 fix round (C1, code-review 1) — A DETAILS SAVE KEEPS HELEN ON THE
   * HOME. A home without a short code is keyed `a<id>`; saving a short code from its
   * Details re-keys its group on the server, and the next column read no longer lists
   * the old key, which the stale-key fallback above read as "this home is gone" and
   * sent her to All homes mid-edit. So the save re-reads the accounts AND the groups
   * together, finds the group whose account is the one she was editing, and reopens the
   * home under its new key: same tab, Details still open. Only a home the server no
   * longer lists at all falls back to All homes.
   */
  const onDetailsSaved = () => {
    reloadBook();
    const editedId = currentAccount?.accountId;
    if (editedId === undefined) return;
    rekeying.current = { to: null };
    // ⭐ board 2812 M5 step 0 (g): this re-read's ticket, and the pick it was asked on.
    const ticket = ++groupsAsked.current;
    const pick = picks.current;
    void Promise.all([listAccounts().catch(() => null), listHomeGroups().catch(() => null)]).then(([accountList, g]) => {
      if (accountList !== null) setAccounts(accountList.rows);
      if (g === null) {
        rekeying.current = null;
        return;
      }
      applyGroups(ticket, g);
      // ⭐ board 2812 M5 step 0 (g, code-review N2): Helen picked another home while this
      // re-read was out, so its answer re-keys NOTHING (it would pull her back).
      if (picks.current !== pick) {
        rekeying.current = null;
        return;
      }
      const fresh = Array.isArray(g.groups) ? g.groups : [];
      const rows = accountList === null ? accounts : accountList.rows;
      const now = fresh.find((x) => accountForKey(rows, x.key)?.accountId === editedId);
      // Held until the host has shown the new key, so the stale-key effect cannot read
      // the old key against the new groups in between.
      rekeying.current = now === undefined || now.key === currentKey ? null : { to: now.key };
      if (now === undefined) openHome(null);
      else if (now.key !== currentKey) rekeyHome(now.key);
    });
  };

  /* ⭐ T1749 M7 (R5) — THE OVERRIDE IS CONSUMED, ONCE. Without this the caller
     would hold `homes` forever and every later arrival at this screen would open
     on the account book, whatever was waiting. Mount-only, beside `load`. */
  useEffect(() => {
    if (openOn !== undefined) onOpened?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /** The caret follows the announcement: the control that caused it is disabled
   *  while the request is in flight, so the browser has already blurred it to
   *  <body>. Keyed on the notice OBJECT (a fresh one per action) so it fires for
   *  every outcome, and not on mount. */
  useEffect(() => {
    if (notice === null) return;
    noticeRef.current?.focus();
  }, [notice]);

  const run = async (
    action: () => Promise<{ ok: true; value: unknown } | { ok: false; message: string }>,
    okText: string,
  ) => {
    if (inFlight.current) return;
    inFlight.current = true;
    setBusy(true);
    setNotice(null);
    try {
      const result = await action();
      if (!result.ok) {
        setNotice({ kind: 'error', text: result.message });
        return;
      }
      setNotice({ kind: 'ok', text: okText });
      load();
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  };

  const homes = state.kind === 'ready' ? state.homes : [];
  const review = state.kind === 'ready' ? state.review : null;

  /**
   * The roster's join, and it is a `Map` for a REASON THAT HAS ALREADY BITTEN
   * THIS CODEBASE ONCE.
   *
   * ⛔ A plain object literal here would make a home named `constructor` (or a
   * nurse keyed by such a string) resolve to an INHERITED FUNCTION rather than
   * `undefined` — not a crash, a silently wrong roster, and the same defect the
   * review marks carried before they were given a null prototype. A `Map` has no
   * prototype chain to walk, so the hazard cannot arise by construction. Do NOT
   * "simplify" this to `Record<string, …>`.
   *
   * ⭐ RETIRED BY T1146 M2. This block used to read "THE JOIN KEY IS THE HOME
   * NAME, and that is forced rather than chosen: the portal's access review
   * groups by `h.name` and returns no home id at all". BOTH HALVES ARE NOW
   * FALSE: `buildAccessReview` groups by `home_id` and carries it as `homeId`,
   * and this screen joins on it.
   *
   * ⚠️ The name map below SURVIVES as the SKEW path only — see `nursesFor`.
   *
   * ⛔ AN EARLIER VERSION OF THIS COMMENT SAID THAT "INHERITS TODAY'S BEHAVIOUR
   * RATHER THAN INVENTING A NEW FAILURE MODE." THAT WAS WRONG, and a security
   * review measured it. The predecessor iterated the portal's GROUPED report, so
   * two same-named homes were already collapsed and their nurses rendered ONCE.
   * This screen iterates the HOME LIST — one row per `home_id` — so the same
   * lookup rendered that one merged list under BOTH blocks: each nurse shown
   * twice, and exported twice, on an Article 9 access review, with one home's
   * staff attributed to another home's row. The MERGE is inherited; the
   * DUPLICATION was new.
   *
   * ⛔ AND THE TRIGGER IS THE CUTOVER ITSELF, not an exotic state.
   * `portal.homes.name` has no unique constraint, production's three homes are
   * all unlinked, and this screen's own bootstrap control MINTED A NEW HOME named
   * after the kiosk account rather than linking the existing one — so an
   * operator following the copy on this screen is how the duplicate gets made.
   * ⚠️ That control was labelled "Bring X in" when this was written; T1146 M2
   * renamed it to "Create a new home for X" and demoted it beneath an
   * attach-to-an-existing-home picker, which is what removes the trap. The
   * history above is why the guard exists and is left standing.
   *
   * ⚠️ SCOPED BY T1146 M2 — this paragraph is now the SKEW-PATH rationale, not
   * the primary one. On the primary path a twin-named home DOES carry its own
   * list, because the join is by id and there is nothing left to guess.
   *
   * On the skew path (an older portal sending no `homeId`) the refusal below
   * still applies unchanged: a name that appears on more than one home carries
   * no nurse list and says why. Suppressing a list we cannot attribute is the
   * safe direction on a recertification surface; showing it under both rows
   * asserts something false. The per-home controls stay live because they
   * address by `home_id` and are unaffected.
   *
   * ⭐ THE DEFERRAL IS DISCHARGED. This block used to read "The real fix is to
   * join by `home_id` … Deferred deliberately, not overlooked". T1146 M2 did it:
   * the portal's report now groups by id and carries it, and this screen joins
   * on it.
   *
   * ⛔ AND THE GUARD BELOW STILL HAS A REASON — a DIFFERENT one, which is why it
   * was rewritten rather than removed. It no longer defends against a name
   * collision on the primary path (an id join cannot collide); it defends the
   * DEPLOY-SKEW window. The recorded order is KIOSK FIRST, THEN PORTAL, so a
   * kiosk that understands `homeId` WILL, for a period, be talking to a portal
   * that does not send it. Deleting `ambiguousHomeNames` on the grounds that
   * "the join is by id now" would make that window collapse every home into one
   * entry — strictly worse than the name collapse M2 removed. Two specs pin it:
   * the id-path arm and the SKEW arm in `nursing-homes.spec.tsx`.
   */
  /** ⭐ T1146 M2 — THE PRIMARY PATH: join by `home_id`, the portal's own key. */
  const nursesByHomeId = new Map<number, PortalAccessNurse[]>();
  /** The SKEW path only — see `nursesFor`. Kept, not deleted. */
  const nursesByHomeName = new Map<string, PortalAccessNurse[]>();
  /**
   * Ids claimed by MORE THAN ONE report entry — the un-attributable set, id side.
   *
   * ⛔ Routing a clash into the name map does NOT fix it: that map is
   * last-write-wins too, so the first roster still disappeared (measured
   * `first=0 second=1`). `home_id` is a primary key, so a duplicate means the
   * payload is wrong, and the only honest response to a wrong payload is to
   * decline to attribute it — the same direction the twin-name refusal takes.
   */
  const clashingHomeIds = new Set<number>();
  for (const h of review?.homes ?? []) {
    // ⛔ `typeof === 'number'`, NOT `!== null`. The projection types this
    // `number | null`, but a stubbed or older payload can carry it ABSENT, and
    // `undefined !== null` is TRUE — which would key the map on `undefined`,
    // miss every real lookup, and silently empty the ENTIRE roster. Measured:
    // it reds 18 specs at once, all of them reading "nobody is set up here".
    // ⚠️ USABILITY of the id is the PROJECTION's job, not this loop's:
    // `projectHome` runs it through `requestId`, so a 0/-1/3.5/1e300 arrives here
    // as `null` and takes the degrade rather than keying a lookup that cannot
    // hit. Do not relax that to `numOrNull` — a security review measured the
    // nurse vanishing from the screen AND the CSV when it was.
    if (typeof h.homeId !== 'number') {
      nursesByHomeName.set(h.homeName, h.nurses);
      continue;
    }
    // ⛔ REFUSE A DUPLICATE ID RATHER THAN OVERWRITING. A bare `Map.set` is
    // last-write-wins, so two entries claiming one `homeId` silently DROPPED the
    // first roster — measured. `home_id` is a PK, so a duplicate means the
    // payload is wrong, and the safe reading of a wrong payload is "we cannot
    // attribute this", never "here is the last one that happened to arrive".
    // Routing the collision to the name map puts it back on the degrade path,
    // where the twin-name refusal can still catch it.
    if (nursesByHomeId.has(h.homeId) || clashingHomeIds.has(h.homeId)) {
      nursesByHomeId.delete(h.homeId);
      clashingHomeIds.add(h.homeId);
      continue;
    }
    nursesByHomeId.set(h.homeId, h.nurses);
  }

  /**
   * Home names carried by MORE THAN ONE home row — the un-attributable set.
   *
   * ⛔ STILL COMPUTED, AND STILL LOAD-BEARING AFTER M2. It no longer guards the
   * primary path (an id-keyed join cannot confuse two homes), but it is the
   * whole of the SKEW path: a portal that predates `homeId` sends nulls, and a
   * kiosk that then keyed on an absent id would collapse EVERY home into one
   * entry — strictly worse than the name collapse this replaced.
   * ⛔ Do not "simplify" it away on the grounds that the join is now by id.
   */
  const ambiguousHomeNames = new Set<string>();
  {
    const seen = new Set<string>();
    for (const h of homes) {
      if (seen.has(h.name)) ambiguousHomeNames.add(h.name);
      else seen.add(h.name);
    }
  }


  /**
   * ⭐ T1146 M2 — the homes an unlinked account can be ATTACHED to.
   *
   * A home already carrying an account is excluded: v7's partial unique index
   * `portal_homes_account_id_key` permits one account per home, so offering a
   * taken home would produce a collision the operator cannot diagnose. Narrowed
   * in JS from the unfiltered list for the same reason `unlinkedAccounts` is —
   * see its comment on the pg-mem `IS NULL` scar.
   */
  const attachableHomes = homes.filter((h) => h.account_id === null);

  /**
   * ⛔ PER-ROW, NOT A WHOLE-REPORT MODE SELECTOR — arrived at the hard way, so it
   * is written down.
   *
   * An earlier version of this code picked ONE join mode for the whole report
   * (`homes.some(h => typeof h.homeId === 'number')`). An output gate mounted
   * this component against a MIXED report — most homes carrying a numeric
   * `homeId`, one carrying `null` — and measured the null-id home's nurses being
   * silently DROPPED while the row rendered the FALSE "Nobody is set up at this
   * home yet." ⛔ A whole-report selector discards every row that does not match
   * the winning mode.
   *
   * ⚠️ That shape cannot arise from today's portal — `home_id` is
   * `NOT NULL GENERATED ALWAYS AS IDENTITY PRIMARY KEY` and one response comes
   * from one process — so it is a producer-defect / hostile-payload state, not a
   * deploy-skew one. Guarded anyway: every other field crossing this seam is
   * treated as hostile, and the failure mode here is an Article 9 surface
   * asserting something FALSE, which is the one outcome it must never produce.
   *
   * ⛔ `nursesByHomeName.size` IS THE DISCRIMINATOR, and it is what makes the
   * per-row form safe. It counts report entries that arrived WITHOUT an id —
   * nurses this screen holds but cannot place:
   *   - `0` — every entry carried an id, so a home not found by id genuinely has
   *     no nurses and "Nobody is set up" is TRUE and must be said.
   *   - `> 0` — some nurses are unplaced. Fall back to the name for those and
   *     keep the twin-name refusal, exactly as before ids existed.
   * Without it, a zero-nurse home sharing a name would announce "another home
   * has this exact name" about an empty roster — the mirror-image false claim.
   *
   * ⛔ THE DEGRADE FOLLOWS `nurseId`'s DISCIPLINE in `portal-ops-projection.ts`
   * exactly — never an aggregate refusal. The recorded deploy order is KIOSK
   * FIRST, THEN PORTAL, so the null case is a guaranteed window rather than an
   * edge case, and blanking the whole review for its duration would regress a
   * surface that renders fine today.
   *
   * ⚠️ THE FALLBACK KEEPS THE OLD REFUSAL, deliberately. On the skew path we
   * still cannot tell two same-named homes apart, so we decline to attribute
   * rather than guess — `nurseId`'s comment states the direction: showing a list
   * we cannot attribute asserts something false, while an empty one merely says
   * we do not know.
   */
  const nursesFor = (home: PortalHome): PortalAccessNurse[] => {
    // ⚠️ `home_id` — PortalHome is the raw snake_case row shape from
    //    /api/ops/homes, NOT the camelCase projection the access review uses.
    if (clashingHomeIds.has(home.home_id)) return [];
    const byId = nursesByHomeId.get(home.home_id);
    if (byId !== undefined) return byId;
    // ⚠️ An earlier revision had `if (nursesByHomeName.size === 0) return [];`
    // here and a comment crediting it with the design's safety. A security review
    // measured it BEHAVIOURALLY INERT — with an empty map both branches below
    // already return `[]` — so it was a guard that could not fail, dressed as one
    // that could. Removed. The size test IS load-bearing, but only in
    // `cannotAttribute`, which is where it now lives.
    return ambiguousHomeNames.has(home.name) ? [] : (nursesByHomeName.get(home.name) ?? []);
  };

  /**
   * Whether to SAY we cannot attribute this home's roster.
   *
   * ⛔ Never true when the id join placed this home, and never true when nothing
   * is unplaced — in both cases claiming ambiguity would itself be false.
   */
  const cannotAttribute = (home: PortalHome): boolean =>
    !nursesByHomeId.has(home.home_id) &&
    nursesByHomeName.size > 0 &&
    ambiguousHomeNames.has(home.name);

  /** A clash on the id itself — a different cause, so a different sentence. */
  const clashedOnId = (home: PortalHome): boolean => clashingHomeIds.has(home.home_id);

  /**
   * Which accounts are not yet in the portal (P0-9's bootstrap half).
   *
   * ⛔ COMPUTED IN JS FROM THE UNFILTERED HOME LIST, NEVER FROM AN `IS NULL`
   * PREDICATE, and that is a scar rather than a preference. This plan's own M1
   * measured pg-mem answering `WHERE account_id IS NULL` FROM THE PARTIAL INDEX
   * — which by construction holds no NULL rows — returning 0 where the truth was
   * 3, in a direction no default-lane test can observe. Production's portal
   * homes are ALL unlinked, so "which homes still need linking?" is precisely
   * the query that silently returns nothing. Reading every home and narrowing
   * here is the recorded safe idiom.
   */
  const linkedAccountIds = new Set<number>();
  for (const h of homes) if (h.account_id !== null) linkedAccountIds.add(h.account_id);
  const unlinkedAccounts = (accounts ?? []).filter(
    (a) => a.active && !linkedAccountIds.has(a.accountId),
  );

  const accountName = (id: number | null): string | null => {
    if (id === null) return null;
    return (accounts ?? []).find((a) => a.accountId === id)?.name ?? null;
  };

  /**
   * The exported sheet — and it is a DIFFERENT ARTIFACT from the one it
   * replaces, which is worth saying plainly rather than implying continuity.
   *
   * What shipped before was a RECOMMENDATION sheet: it carried the reviewer's
   * per-nurse Keep/Remove marks and existed so a home's administrator could act
   * on them. Under Alex's D4-b ruling those marks are gone — the screen now
   * carries a control that REALLY removes access, and an advisory control
   * inches from a working one is exactly the confusion the findings note warned
   * about. So the sheet survives as an export of CURRENT ROSTER STATE, with no
   * decision column at all. The capability ("CSV copy + download", listed in
   * the audit as missing kiosk-side) is retained; the inertness is what went.
   *
   * ⛔ `cell()`'s FORMULA NEUTRALISER SHIPS WITH IT, unchanged. This is one of
   * the two security regressions that had to survive D4 by name. Quoting and
   * formula-neutralising are two different jobs: quotes make a parser read the
   * cell correctly, they do NOT stop Excel treating a cell BEGINNING `= + - @`
   * (or tab / CR) as a formula, and this sheet is emailed to a nursing-home
   * administrator who opens it in Excel. Kept identical to
   * portal/src/core/access-review.ts's `csvCell`.
   */
  function buildCsv(): string {
    if (review === null) return '';
    const cell = (v: string) => `"${(/^[=+\-@\t\r]/.test(v) ? `'${v}` : v).replace(/"/g, '""')}"`;
    const lines = [
      ['Home', 'Nurse', 'Work email', 'Last used the portal', 'Devices', 'Has a PIN', 'Can sign in', 'Flag']
        .map(cell)
        .join(','),
    ];
    for (const home of homes) {
      // Same attribution rule as the screen: a list we cannot attribute is
      // omitted rather than printed under a home it may not belong to. The
      // sheet leaves the organisation, so this is the direction that matters.
      for (const n of nursesFor(home)) {
        lines.push([
          cell(home.name),
          cell(n.displayName),
          cell(n.email),
          cell(n.lastActiveAt === null ? 'never signed in' : n.lastActiveAt.slice(0, 10)),
          cell(String(n.deviceCount)),
          cell(n.hasPin ? 'yes' : 'no'),
          // ⛔ THREE-WAY. `n.active ? …` would export "no - access removed" for
          // a row the portal never stated, fabricating a removal in a sheet
          // that leaves the organisation.
          cell(
            n.active === true
              ? 'yes'
              : n.active === false
                ? 'no - access removed'
                : 'unknown - not stated by the portal',
          ),
          cell(n.dormant ? `no activity in ${String(review.dormantAfterDays)} days` : ''),
        ].join(','));
      }
    }
    return lines.join('\n') + '\n';
  }

  const [csvCopied, setCsvCopied] = useState<string | null>(null);

  async function onCopyCsv(): Promise<void> {
    const text = buildCsv();
    try {
      await navigator.clipboard.writeText(text);
      // Value-tagged, not a boolean cleared by an effect: Preact defers effects,
      // so a stale-clearing run lands AFTER the operator has pressed Copy and
      // wipes the confirmation she was just given (measured, Operators.tsx).
      setCsvCopied(text);
    } catch {
      setCsvCopied(null);
      setNotice({ kind: 'error', text: 'Could not copy the sheet. Use Download the sheet instead.' });
    }
  }

  function downloadCsv(): void {
    if (review === null) return;
    const blob = new Blob([buildCsv()], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    // A different name for a different artifact — see buildCsv's header.
    a.download = `nurse-roster-${review.generatedAt.slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }

  const onAddNurse = (home: PortalHome) => {
    const name = nurseName.trim();
    const email = nurseEmail.trim();
    const group = nurseGroup.trim();
    if (name === '' || email === '') {
      setNotice({ kind: 'error', text: 'Both a name and a work email address are needed.' });
      return;
    }
    // ⭐ T2075 M2 — a group sign-in needs its group's name; the kiosk route and
    // the portal's ops door both refuse without it, so refuse here first.
    if (nurseRole === 'group' && group === '') {
      setNotice({ kind: 'error', text: 'A group sign-in needs the name of its group.' });
      return;
    }
    void run(async () => {
      const r = await createPortalNurse(home.home_id, email, name, nurseRole, nurseRole === 'group' ? group : null);
      if (r.ok) {
        setNurseName('');
        setNurseEmail('');
        setNurseRole('group');
        setNurseGroup('');
        setAddingAt(null);
      }
      return r;
    }, `${name} can sign in for ${home.name} now.`);
  };

  /**
   * ⭐ board 2812 M4 — ONE PORTAL HOME'S BLOCK (its account link, its pickup offset and
   * phone, and who can sign in), as a function, so the set-up view's roster and a home's
   * Details (`HomePage.tsx`) render the same controls from the same state. Unchanged
   * inside: it was the body of the roster's map.
   */
  const rosterBlock = (home: PortalHome, homeIndex: number): VNode => {
    /* ⛔⛔ THE FIELD IDS ON THIS ROW ARE KEYED BY THE HOME'S POSITION,
       NEVER BY `home_id` (T1749 M7 rework 1, the M7 adoption criterion:
       no field id is built from wire data). Several homes render at
       once, so a constant id would collide; a wire id would make the
       uniqueness of a DOM id a property of the server's answer and
       would publish a database key into the class/id surface the copy
       gates do not read. The per-screen duplicate-id arm in
       `nursing-homes.spec.tsx` is what holds it. */
    const homeKey = `home-${String(homeIndex)}`;
    const nurses = nursesFor(home);
    const ambiguous = cannotAttribute(home);
    const idClash = clashedOnId(home);
    const linkedTo = accountName(home.account_id);
    return (
      <div key={home.home_id} class="op-review-home">
        <h3 class="screen-sub screen-sub--item">{home.name}</h3>

        {/* ⛔ THE DISCRIMINATOR, and it exists because M2 REMOVED a
            signal while improving attribution. Before the id join, two
            same-named homes carried a visible "Another home has this
            exact name" line. Now each correctly shows its own roster —
            and a security review measured the consequence: two
            identical headings, identical accessible names on every
            write control ("Add a nurse at X" twice), and no way to tell
            which row is which. Improving attribution must not cost the
            operator the ability to tell two homes apart.
            ⚠️ Shown ONLY when the name is actually shared, so the
            ordinary single-home case gains no clutter — this screen's
            simplicity constraint is a hard one.
            ⛔ The TOWN, never the id. `homeId` is a join key and must
            not reach the DOM; the town is already a field the operator
            typed and is exactly what distinguishes two branches. */}
        {ambiguousHomeNames.has(home.name) && (
          <p class="op-login" data-fb-opaque /* town is an address fragment */>
            In {home.town}. Another home shares this name.
          </p>
        )}

        {/* Which account this home bills to — the link P0-9 is about,
            and the surface on which a MIS-link is visible at all. */}
        <p class="op-login">
          {home.account_id === null
            ? 'Not linked to a customer account yet.'
            : `Linked to ${linkedTo ?? `account ${String(home.account_id)}`}.`}
        </p>

        <div class="op-add">
          {/* ⛔ data-fb-opaque: the click-trail recorder reads a
              <select>'s textContent, which is EVERY option
              concatenated — so without this the whole customer book
              lands in the kiosk's click stream. A recorded trap, not
              a precaution.
              ⚠️ CORRECTED 2026-08-14 (T1076, board 900): this named
              "feedback_submissions.click_stream". On the KIOSK the
              click stream goes into the locally-downloaded bundle
              (feedback/bundle.ts), never that table — whose only
              writer is the SITE path. The trap is unchanged. */}
          {/* ⛔ `<label>` IS IN THE RECORDER'S INTERACTIVE SELECTOR
              (feedback/clicks.ts), so `describeTarget` reads its
              textContent — measured as
              "label.Customer account for St. Colman's Nursing Home".
              A home name is an organisation, a rank below the nurse
              names on the buttons below, but it is still an
              identifiable party in an Article 9 context and
              `redactSensitive` catches neither. Same for the three
              home/account-named buttons on this screen. */}
          {/* ⭐⭐ T1749 M7 rework 1 — `FormField`, and `opaque` is the prop
              that made adoption possible here: this label INTERPOLATES
              the home's name and `<label>` is in the recorder's own
              interactive selector, so both the label and the control
              have to carry the mark or a click writes the home's name
              into the click stream. */}
          <FormField
            id={`${homeKey}-link`}
            label={`Customer account for ${home.name}`}
            kind="select"
            opaque
            disabled={busy}
            value={linkChoice[home.home_id] ?? String(home.account_id ?? '')}
            options={[
              { value: '', label: 'Not linked' },
              ...(accounts ?? []).map((a) => ({ value: String(a.accountId), label: a.name })),
            ]}
            onInput={(v: string) =>
              setLinkChoice((prev) => ({ ...prev, [home.home_id]: v }))
            }
          />
          <button
            type="button"
            class="btn btn--quiet btn--inline"
            disabled={busy}
            data-fb-opaque /* home name in the label — see the <label> above */
            onClick={() => {
              const raw = linkChoice[home.home_id] ?? String(home.account_id ?? '');
              const next = raw === '' ? null : Number(raw);
              void run(
                () => linkPortalHome(home.home_id, next),
                next === null
                  ? `${home.name} is no longer linked to an account.`
                  : `${home.name} is linked to ${accountName(next) ?? 'that account'}.`,
              );
            }}
          >
            Save the account for {home.name}
          </button>
        </div>

        {/* ⭐ T1868 M4 — the per-home settings, fence-sanctioned by
            Captain on 2026-09-08 (Q112(b)). ⛔ FIELDS ON THE EXISTING
            HOMES TAB, reusing this screen's own `field-label` /
            `login-input` / `btn btn--quiet btn--inline` markup: P0-7
            forbids a new button, card or chip, and #1749 owns the
            design system, so nothing new is introduced here. The
            labels name the home for the same reason the account
            control above does — several homes render at once, so a
            bare "Pickup offset" would be ambiguous to a screen
            reader. */}
        {/* ⛔ `op-add`, the SAME wrapper the account-link block above
            uses — not a new class. P0-7 forbids a new component, card
            or chip, and `style-contract.spec.ts` enforces the harder
            version of that rule: every class the markup applies must
            have a rule behind it. A `class="field"` here reds it,
            which is how this was caught rather than shipped. */}
        <div class="op-add">
          {/* ⭐ P2-1 — the seasonal note. It says WHY the setting exists
              and WHEN to revisit it, because an offset nobody remembers
              the reason for is one nobody ever tunes. It is the field's
              HINT now (T1749 M7 rework 1), so `aria-describedby` is
              derived rather than hand-wired — the WCAG 1.3.1 property it
              was written for is kept by construction.
              ⚠️ `min` / `max` DO NOT SURVIVE, and the field's own note
              said why before the move: they are constraint VALIDATION,
              which needs a `<form>` submit this screen never does, so
              they were decorative. The handler's check is the authority
              and is untouched. */}
          <FormField
            id={`${homeKey}-offset`}
            label={`Pickup offset for ${home.name} (minutes)`}
            type="number"
            inputMode="numeric"
            opaque
            disabled={busy}
            hint={'How long before the appointment the bus collects. The nurse sees this as her '
              + 'starting pickup time and can change it on the day. Worth revisiting when the '
              + 'schools go back, because traffic in those months often needs 75 to 80 minutes.'}
            value={offsetChoice[home.home_id] ?? String(home.pickup_offset_minutes ?? 60)}
            onInput={(v: string) =>
              setOffsetChoice((prev) => ({ ...prev, [home.home_id]: v }))
            }
          />

          {/* ⛔ THE BOX CANNOT GROW PAST THE ROUTE'S CAP, and `maxLength`
              SURVIVES the adoption where the offset's `min`/`max` did
              not: those are constraint VALIDATION, which needs a
              `<form>` submit this screen never does, whereas `maxlength`
              is an input-LIMITING behaviour the browser applies to
              typing and pasting regardless. It still cannot be the
              authority — it does not bind a value the SERVER put in the
              box, and it counts UTF-16 code units where the route counts
              characters — but both gaps run in the safe direction, and
              the handler's own check below refuses by name. */}
          <FormField
            id={`${homeKey}-phone`}
            label={`Phone number for ${home.name}`}
            type="tel"
            maxLength={FALLBACK_PHONE_MAX}
            opaque
            disabled={busy}
            hint={'The number to ring at the home about a job. Reception is usually right: it is '
              + "the home you are ringing, not a resident's family."}
            value={phoneChoice[home.home_id] ?? home.fallback_phone ?? ''}
            onInput={(v: string) =>
              setPhoneChoice((prev) => ({ ...prev, [home.home_id]: v }))
            }
          />

          <button
            type="button"
            class="btn btn--quiet btn--inline"
            disabled={busy}
            data-fb-opaque /* home name in the labels — see above */
            onClick={() => {
              const rawOffset =
                offsetChoice[home.home_id] ?? String(home.pickup_offset_minutes ?? 60);
              const rawPhone = phoneChoice[home.home_id] ?? home.fallback_phone ?? '';
              const offset = Number(rawOffset);
              // ⚠️ An emptied box CLEARS the number: null is the
              // correction path, and without it a wrong number once
              // typed could never be removed. ⛔ COMPUTED HERE RATHER
              // THAN BESIDE ITS `settings` ASSIGNMENT, because the two
              // content pre-checks below need it — and a `null` must
              // reach them as "she cleared it", never as a value to
              // check for digits.
              const nextPhone = rawPhone.trim() === '' ? null : rawPhone;
              // ⛔ REFUSED HERE TOO, not only at the route. The route
              // is the authority and refuses by name, but letting a
              // plainly empty or non-numeric box travel would show
              // Helen a server error for something the screen could
              // have told her instantly.
              // ⛔ THE MESSAGE NAMES THE HOME, and that is not
              // decoration. Several homes stack on this screen and the
              // banner sits at the top, so an error raised on the
              // third home scrolls Helen back to the first with no way
              // to tell which Save button she must return to —
              // measured on the live seat with four homes. The success
              // message already names the home; the refusals now match.
              // ⛔ AND THE RANGE IS CHECKED HERE TOO. `min`/`max` on
              // the input are decorative: there is no <form>, and the
              // handler reads `value` directly, so native constraint
              // validation never runs. Without this, -5 and 999
              // round-trip to the server for no reason.
              if (
                rawOffset.trim() === '' ||
                !Number.isInteger(offset) ||
                offset < 1 ||
                offset > 480
              ) {
                setNotice({
                  kind: 'error',
                  text: `Give the pickup offset for ${home.name} as a whole number of minutes between 1 and 480.`,
                });
                return;
              }
              /**
               * ⛔⛔ THE PHONE'S TWO CONTENT RULES ARE PRE-CHECKED HERE
               * TOO, AND THIS BLOCK EXISTS BECAUSE THE ux ROUND FOUND
               * THEM REPRODUCING THE EXACT DEFECT THE OFFSET'S OWN
               * PRE-CHECK WAS WRITTEN TO CLOSE (T1868 M6, 2026-09-09).
               *
               * The two rules ship SERVER-SIDE in the portal
               * (`opsUpdateHomeSettings`) and are forwarded to this
               * banner verbatim, so they arrived NAMING NO HOME —
               * while the banner sits at the top of a list of nine-plus
               * homes and the save jumps the operator's view back to it.
               * ⛔ THE COMMENT ON THE OFFSET ABOVE ALREADY MEASURED THIS
               * ON THE LIVE SEAT WITH FOUR HOMES: "an error raised on
               * the third home scrolls Helen back to the first with no
               * way to tell which Save button she must return to". I
               * added two messages and did not carry the fix.
               *
               * ⚠️ THE ROUTE REMAINS THE AUTHORITY and refuses by name;
               * this is the same belt the offset wears, for the same
               * reason — a plainly bad box should not travel and come
               * back as a nameless banner.
               * ⛔ AND IF THE TWO EVER DRIFT, THE SERVER'S MESSAGE STILL
               * ARRIVES UNNAMED. That is deliberate and is a signal
               * rather than a residual defect: an unfamiliar, home-less
               * refusal on this screen means the client and the route
               * disagree, which is exactly the thing worth noticing.
               */
              // ⛔ LENGTH FIRST, BECAUSE THE ROUTE CHECKS IT FIRST.
              // `opsUpdateHomeSettings` grades length, then `@`, then
              // digits, and returns the FIRST failure only. If this
              // screen picked a different one of the three, Helen
              // would fix what she was told about and immediately
              // meet a second, different refusal from the server for
              // a string she had already been shown as acceptable.
              // ⛔ `Array.from`, NOT `.length` — see the constant.
              // ⚠️ AND IT MEASURES THE RAW BOX, where the route
              // measures after `cleanStoredText`. Cleaning only ever
              // REMOVES (NULs, bidi overrides), so on LENGTH this can
              // only ever refuse a pathological string the route would
              // have accepted after cleaning. Unreachable by typing,
              // and the route stays the authority.
              // ⛔ BUT THE "EMPTY" TEST IS NOT IN THAT SAFE CLASS, AND
              // AN EARLIER VERSION OF THIS COMMENT CLAIMED IT WAS
              // (output-gate-review, M7, measured on 5 probe inputs,
              // 3 of which disagreed). This screen decides "she
              // cleared it" with `rawPhone.trim() === ''`; the route
              // decides it with `cleanStoredText(raw) === null`, which
              // ALSO purges NULs, control, bidi and zero-width
              // characters. ⇒ A box holding ONLY a NUL or a lone
              // zero-width space is "not empty" here and "empty" at
              // the route: this screen refuses it with the digits
              // message, where the route would have cleared the
              // number. That is a FALSE REFUSAL — friction, never data
              // loss and never a bypass — on an input no keyboard
              // produces and only a paste can carry. It is left alone
              // deliberately: matching it would mean reimplementing
              // `cleanStoredText` in the kiosk bundle, which cannot
              // import it (portal/ is not in this image), and a
              // second copy of a text cleaner is a worse bug than a
              // refusal nobody can reach by typing.
              if (
                nextPhone !== null &&
                Array.from(nextPhone).length > FALLBACK_PHONE_MAX
              ) {
                setNotice({
                  kind: 'error',
                  text: `Keep the phone number for ${home.name} to ${String(FALLBACK_PHONE_MAX)} characters or fewer.`,
                });
                return;
              }
              if (nextPhone !== null && nextPhone.includes('@')) {
                setNotice({
                  kind: 'error',
                  text: `Give a phone number for ${home.name}. An email address belongs somewhere else.`,
                  // ⛔ TWO SENTENCES, NOT AN EM DASH. `check:copy`
                  // bans the em dash in client UI source and this
                  // string carried one from M6 — the gate is CI-only
                  // (CLAUDE.md §4: "run it before a UI gate"), so it
                  // was never run in the milestone that wrote it and
                  // the red arrived here at M7's copy audit. The
                  // split also matches the ROUTE's own wording for
                  // the same refusal, which was already two sentences.
                });
                return;
              }
              if (nextPhone !== null && !/\d/.test(nextPhone)) {
                setNotice({
                  kind: 'error',
                  text: `Give the phone number for ${home.name}, with the digits.`,
                });
                return;
              }
              // ⛔ ONLY THE KEYS THE OPERATOR ACTUALLY TOUCHED. The
              // seam distinguishes an ABSENT key from an explicit
              // null the whole way down, and sending both every time
              // gave that distinction NO CLIENT — it was reachable
              // only by a direct API caller (M4 security review, M2).
              // Worse, an unconditional send makes Save a
              // last-write-wins clobber: two operators on the same
              // home silently overwrite each other, and with no audit
              // row and no updated_at on portal.homes that is
              // undetectable. Sending only what changed shrinks the
              // clobber to the field actually edited.
              const settings: {
                pickupOffsetMinutes?: number;
                fallbackPhone?: string | null;
              } = {};
              if (offset !== (home.pickup_offset_minutes ?? 60)) {
                settings.pickupOffsetMinutes = offset;
              }
              if (nextPhone !== (home.fallback_phone ?? null)) {
                settings.fallbackPhone = nextPhone;
              }
              if (
                settings.pickupOffsetMinutes === undefined &&
                settings.fallbackPhone === undefined
              ) {
                setNotice({
                  kind: 'ok',
                  text: `Nothing to change for ${home.name}.`,
                });
                return;
              }
              void run(
                () => savePortalHomeSettings(home.home_id, settings),
                `Saved the settings for ${home.name}.`,
              );
            }}
          >
            Save the settings for {home.name}
          </button>
        </div>

        {idClash ? (
          /* ⛔ A DIFFERENT CAUSE NEEDS A DIFFERENT SENTENCE. Saying
             "another home has this exact name" here would be a false
             statement about WHY — the names may be unique and the ids
             are what collided. `home_id` is a primary key, so this can
             only be a producer defect, and it names one. */
          <p class="helper-note">
            The portal sent two different lists for this home, so we cannot tell which
            one is right and neither is shown. Tell Alex.
          </p>
        ) : ambiguous ? (
          /* ⛔ NOT "nobody is set up here" — that would be a second
             false statement.
             ⚠️ T1146 M2: reachable ONLY on the deploy-skew path now.
             The portal groups its roster by `home_id` and carries it,
             so on the primary path this branch is unreachable and each
             twin renders its own list. It survives because an older
             portal sends no id, and then the screen genuinely cannot
             say which of two same-named homes a nurse belongs to. */
          <p class="helper-note">
            Another home has this exact name, so the portal cannot say which of them
            each nurse belongs to and this list is not shown. Rename one of them, or
            ask Alex to merge them.
          </p>
        ) : nurses.length === 0 ? (
          <p class="helper-note">Nobody is set up at this home yet.</p>
        ) : (
          <ul class="op-list" role="list">
            {nurses.map((n) => (
              <li key={n.email} class="op-row">
                <span class="op-name">{n.displayName}</span>
                <span class="op-login">{n.email}</span>
                <span class="op-login">
                  {n.lastActiveAt === null
                    ? 'Never signed in'
                    : `Last used ${shortDateTime(n.lastActiveAt)}`}
                </span>
                <span class="op-login">
                  {n.deviceCount === 1 ? '1 device' : `${String(n.deviceCount)} devices`}
                </span>
                {n.active === false && <span class="chip chip--quiet">No access</span>}
                {/* ⛔ THREE-WAY, and `!n.active` would collapse it back
                    to two. `null` means the portal did not state it —
                    saying "No access" there asserts a removal that may
                    not have happened. */}
                {n.active === null && <span class="chip chip--quiet">Access unknown</span>}
                {n.dormant && n.active === true && (
                  <span class="chip chip--quiet">
                    No activity in {review?.dormantAfterDays} days
                  </span>
                )}
                {n.hasPin && <span class="chip chip--quiet">Has a PIN</span>}
                <span class="op-decision">
                  {/* ⛔ NO CONTROL THAT LOOKS ACTIONABLE AND IS NOT
                      (P0-6). A nurse the portal did not vouch for
                      carries no id, so there is nothing to call — the
                      row says so in words instead of offering a button
                      that cannot work. */}
                  {n.nurseId === null ? (
                    <span class="op-login">Cannot be changed from here</span>
                  ) : removingNurse !== null && removingNurse === n.nurseId ? (
                    /* ⭐⭐ T1749 M7 rework 1 — THE CONFIRM IN PLACE. It
                       REPLACES the control that armed it, so nothing
                       moves on the rest of the roster and the question is
                       asked where she just tapped. The one accountability
                       fact lives HERE and nowhere else: it used to be a
                       standing paragraph above the whole list, which
                       explained a control the reader had not reached. */
                    <ConfirmInPlace>
                      <button
                        type="button"
                        class="btn btn--quiet btn--inline btn--destructive"
                        disabled={busy}
                        data-fb-opaque
                        onClick={() => {
                          setRemovingNurse(null);
                          void run(
                            () => setPortalNurseActive(n.nurseId as number, false),
                            `${n.displayName} can no longer sign in.`,
                          );
                        }}
                      >
                        Yes, remove access
                      </button>
                      <button
                        type="button"
                        class="btn btn--quiet btn--inline"
                        disabled={busy}
                        onClick={() => setRemovingNurse(null)}
                      >
                        Keep it
                      </button>
                      <p class="field-hint">Recorded against your name.</p>
                    </ConfirmInPlace>
                  ) : (
                    <button
                      type="button"
                      class={n.active === false ? 'btn btn--quiet btn--inline' : 'btn btn--quiet btn--inline btn--destructive'}
                      disabled={busy}
                      /* ⛔ NAMED PER NURSE, so a screen-reader user
                         tabbing a home's list hears WHOSE access each
                         control is about rather than the same two
                         words over and over. */
                      aria-label={
                        n.active === false
                          ? `Restore access for ${n.displayName}`
                          : `Remove access for ${n.displayName}`
                      }
                      /* ⛔ The aria-label carries a NURSE'S NAME and
                         the click recorder prefers aria-label over
                         textContent, so without this a click persists
                         "button.Remove access for Mary O'Sullivan"
                         into the kiosk's click stream.
                         `redactSensitive` catches emails and long
                         digit runs and NEVER names.
                         ⚠️ CORRECTED 2026-08-14 (T1076, board 900):
                         this named "feedback_submissions.click_stream";
                         the kiosk stream lands in the locally-
                         downloaded bundle, not that table. The
                         name-leak trap is unchanged. */
                      data-fb-opaque
                      onClick={() => {
                        // ⛔⛔ A REMOVAL ARMS A CONFIRM; A RESTORE DOES NOT
                        // (T1749 M7 rework 1). Removing access was one tap
                        // into a journalled, append-only write with no undo
                        // afterwards, which is what §5.1 asks a destructive
                        // to confirm. Putting access BACK is not
                        // destructive and keeps its one tap.
                        if (n.active === false) {
                          void run(
                            () => setPortalNurseActive(n.nurseId as number, true),
                            `${n.displayName} can sign in again.`,
                          );
                          return;
                        }
                        setRemovingNurse(n.nurseId);
                      }}
                    >
                      {n.active === false ? 'Restore access' : 'Remove access'}
                    </button>
                  )}
                </span>
              </li>
            ))}
          </ul>
        )}

        {addingAt === home.home_id ? (
          <div class="op-add">
            {/* Mirrors the "Add someone" idiom rather than inventing a
                second form language (P1-2): `login-input`, an
                aria-label AND a placeholder, a type="button", the
                shared run() latch and one notice banner. */}
            <input
              class="login-input"
              type="text"
              placeholder="Mary Byrne"
              aria-label={`Nurse's name at ${home.name}`}
              autocomplete="off"
              value={nurseName}
              data-fb-opaque
              onInput={(e) => setNurseName((e.target as HTMLInputElement).value)}
            />
            <input
              class="login-input"
              type="email"
              placeholder="mary.byrne@example.ie"
              aria-label={`Nurse's work email at ${home.name}`}
              autocomplete="off"
              autocapitalize="none"
              spellcheck={false}
              value={nurseEmail}
              data-fb-opaque
              onInput={(e) => setNurseEmail((e.target as HTMLInputElement).value)}
            />
            {/* ⭐ T2075 M2 (plan D6, R-10 / DS-6) — THE SIGN-IN TYPE AND THE
                GROUP NAME, as the library controls this file already
                wears above (`FormField kind="select"` for the account
                link, `FormField` for the offset), never a raw
                `login-input`. Group sign-in is the default: it is what
                every account was before v13. The label names the home
                for the reason the account control does — several homes
                render at once — and carries `opaque` for the same
                click-stream reason. */}
            <FormField
              id={`${homeKey}-signin-type`}
              label={`Sign-in type at ${home.name}`}
              kind="select"
              opaque
              disabled={busy}
              value={nurseRole}
              options={[
                { value: 'group', label: 'Group sign-in' },
                { value: 'reception', label: 'Reception' },
                { value: 'admin', label: 'Administrator' },
              ]}
              onInput={(v: string) => setNurseRole(v as PortalNurseRole)}
            />
            {nurseRole === 'group' ? (
              <FormField
                id={`${homeKey}-group-name`}
                label={`Group name at ${home.name} (e.g. Rehab)`}
                type="text"
                maxLength={60}
                opaque
                disabled={busy}
                autoComplete="off"
                value={nurseGroup}
                onInput={(v: string) => setNurseGroup(v)}
              />
            ) : null}
            <p class="helper-note">
              She gets a sign-in code at this address. One person can only be at one
              home. An administrator manages the home's staff list and sign-in groups in
              the portal.
            </p>
            <button
              type="button"
              class="btn btn--primary"
              disabled={busy}
              onClick={() => onAddNurse(home)}
            >
              Add this nurse
            </button>
            <button
              type="button"
              class="btn btn--quiet btn--inline"
              disabled={busy}
              onClick={() => setAddingAt(null)}
            >
              Cancel
            </button>
          </div>
        ) : (
          <button
            type="button"
            class="btn btn--quiet btn--inline"
            disabled={busy}
            data-fb-opaque /* home name in the label */
            onClick={() => {
              setNurseName('');
              setNurseEmail('');
              setNurseRole('group');
              setNurseGroup('');
              setAddingAt(home.home_id);
            }}
          >
            Add a nurse at {home.name}
          </button>
        )}
      </div>
    );
  };

  // -------------------------------------------------------------------------
  // ⭐⭐ board 2812 M4 — THE COLUMN OF HOMES AND THE TWO LEVELS (plan steps 2 to 4)
  // -------------------------------------------------------------------------
  const homeGroups = groups.kind === 'ready' ? groups.groups : [];
  const currentGroup = currentKey === null ? null : homeGroups.find((g) => g.key === currentKey) ?? null;
  /**
   * ⭐ board 2812 M4 fix round — A HOME'S ACCOUNT COMES FROM THE ACCOUNT BOOK'S OWN READ,
   * which does not wait on the portal. `accounts` above rides the ROSTER's `Promise.all`,
   * whose two portal reads are essential, so a dead portal made it `null` and a home's
   * Prices, Invoices and Details said "could not be read" about an account the kiosk's
   * own database had just served (measured in `account-book-scaffolding.spec.tsx`, whose
   * stub leaves the portal down on purpose: R5's promise that a dead portal hides no
   * account or price). The roster's copy is the fallback when the book's read failed;
   * `undefined` means neither has answered YET (a tab then says it is getting it).
   */
  const pageAccounts: ApiAccount[] | null | undefined = book.loaded
    ? (book.failed.accounts ? (accounts ?? null) : book.accounts)
    : (accounts === null ? undefined : accounts);
  const currentAccount = currentKey === null ? null : accountForKey(pageAccounts, currentKey);
  const pageAccount = currentKey !== null && pageAccounts === undefined ? undefined : currentAccount;
  /** The portal homes this home's account is linked to (its Details' settings). */
  const linkedPortalHomes = currentAccount === null ? [] : homes.filter((h) => h.account_id === currentAccount.accountId);
  /* ⭐ board 2812 M4 fix round (C4, code-review 5): "Find a home" shows past eight homes
     ONLY, and a term typed while it showed stops filtering when it hides (the effect
     below empties it), so the column never hides homes behind a field that is gone. */
  const findShown = homeGroups.length > 8;
  useEffect(() => {
    if (!findShown && homeFind !== '') setHomeFind('');
  }, [findShown]);
  const needle = findShown ? homeFind.trim().toLowerCase() : '';
  const matches = needle === ''
    ? homeGroups
    : homeGroups.filter((g) => g.name.toLowerCase().includes(needle) || (g.town ?? '').toLowerCase().includes(needle));
  /* ⭐ board 2812 M4 fix round (C3, webapp 1): the home on screen ALWAYS shows in the
     column (current), whatever "Find a home" holds: a page whose home is missing from
     the column says Helen is nowhere. */
  const shownGroups = currentKey === null || matches.some((g) => g.key === currentKey)
    ? matches
    : homeGroups.filter((g) => g.key === currentKey || matches.includes(g));
  const noHomeMatches = needle !== '' && matches.length === 0;
  const figure = (n: number | null) => (n === null ? '!' : String(n));
  /* ⭐ board 2812 M4 fix round (L10, label in name, WCAG 2.5.3): the spoken name holds
     everything the item SHOWS, its town included, before "count unavailable". */
  const spoken = (name: string, town: string | null, n: number | null): string | undefined => (
    n === null ? `${[name, town].filter((x) => x !== null && x !== '').join(', ')}, count unavailable` : undefined
  );
  const columnItems: SubRailItem[] = [
    {
      key: 'all-homes',
      label: (
        <>
          <span class="homes-col-who"><span class="homes-col-name">{HOME_PAGE_WORDS.allHomes}</span></span>
          <span class="homes-col-count">{figure(waitingRequests)}</span>
        </>
      ),
      ariaLabel: spoken(HOME_PAGE_WORDS.allHomes, null, waitingRequests),
      current: !setupOpen && currentKey === null,
      onSelect: () => openHome(null),
    },
    ...shownGroups.map((g) => ({
      key: g.key,
      label: (
        <>
          <span class="homes-col-who">
            <span class="homes-col-name">{g.name}</span>
            {g.town !== null && g.town !== '' && <span class="homes-col-town">{g.town}</span>}
          </span>
          {/* ⭐ R-2812-a: the need-you-now count as a navy figure; "!" when unknown. */}
          {g.needYouNow !== 0 && <span class="homes-col-count">{figure(g.needYouNow)}</span>}
        </>
      ),
      ariaLabel: spoken(g.name, g.town, g.needYouNow),
      current: !setupOpen && currentKey === g.key,
      onSelect: () => openHome(g.key),
    })),
    // ⭐ the requests no home's page shows: their portal home is linked to no account, or
    // (board 2812 fix round, Q273(a)) to an account not marked as a nursing home. They are
    // in All homes' To do, and the fix is in Set up homes and places: link the home, or
    // mark its account as a nursing home.
    ...(groups.kind === 'ready' && groups.notLinked !== null && groups.notLinked > 0
      ? [{
        key: 'not-linked',
        label: (
          <>
            <span class="homes-col-who"><span class="homes-col-name">{HOME_PAGE_WORDS.notLinked}</span></span>
            <span class="homes-col-count">{String(groups.notLinked)}</span>
          </>
        ),
        current: false,
        onSelect: () => openSetUp('homes'),
      }]
      : []),
  ];
  const column = (
    <SubRail
      label="Homes"
      items={columnItems}
      find={findShown ? (
        <>
          <FormField
            id="home-find"
            label={HOME_PAGE_WORDS.findHome}
            placeholder={HOME_PAGE_WORDS.findHint}
            value={homeFind}
            onInput={setHomeFind}
            autoComplete="off"
          />
          {/* ⭐ board 2812 M4 fix round (C4): a term that matches no home says so, under
              the field, rather than leaving a column that looks empty. A live region that is
              always there (so the words are announced when they arrive), and a SPAN: an
              empty `<p>` kept its 20px margin and pushed the column's list down (measured
              on the seat at 1024x700: the list's top 218 against 198). */}
          <span class="helper-note" role="status">{noHomeMatches ? HOME_PAGE_WORDS.noHomeMatches : ''}</span>
        </>
      ) : undefined}
      foot={(
        <>
          {groups.kind === 'error' && <p class="helper-note">{HOME_PAGE_WORDS.homesUnread}</p>}
          <Button variant="quiet" onClick={() => openSetUp('homes')}>{HOME_PAGE_WORDS.setUp}</Button>
        </>
      )}
    />
  );

  const noticeBanner = notice && (
    <Banner kind={notice.kind} elementRef={noticeRef} tabIndex={-1}>
      {notice.text}
    </Banner>
  );
  const keyOf = currentKey ?? 'all';
  /** Back from a booking opened on a tab returns to that tab (board 2812 M3 item 5,
   *  M4 step 0 (g)), and to this home (the store keeps `viewHomeKey`). */
  const openBookingFrom = (from: TabId) => (bookingId: number) => onOpenBooking(bookingId, from);
  const toDoCount = currentKey === null ? waitingRequests : (currentGroup?.needYouNow ?? null);
  /** ⭐ board 2812 M4 fix round (L10): while the column's read is still landing with a home
   *  showing, its count is not known YET, which is not a failure: the tab says "To do"
   *  rather than flashing "(count unavailable)". */
  const countPending = currentKey !== null && groups.kind === 'loading';

  let tabContent: ComponentChildren = null;
  if (tab === 'todo') {
    tabContent = (
      <TodoPiles
        key={`todo-${keyOf}`}
        channel="homes"
        registry={registry}
        onOpenBooking={openBookingFrom('todo')}
        onChanged={onChanged}
        beside
        {...(currentKey === null ? {} : { home: currentKey, onStale: () => openHome(null) })}
        homesNeed={{
          count: toDoCount,
          render: (ctl) => (
            <PortalRequests
              /* ⭐ board 2812 M3 fix round 2 (WT-2): the To do's own `onChanged`, which
                 reloads its piles and then calls `onRequestsChanged` (the badge). */
              onChanged={ctl.onChanged}
              /* ⭐ Board 2814 M8R1 (R3), kept at board 2812 M5's Keep current: App's
                 portal-count poll tick, so the request rows reload on the poll that
                 moves the count. */
              pollTick={portalPollTick}
              onOpenBooking={openBookingFrom('todo')}
              registry={registry}
              /* ⭐ T1868 M6 — the homes this screen has ALREADY loaded, handed down
                 so the requests tab can put the home's own number on the row
                 without fetching one itself. ⚠️ `[]` until this screen's load
                 resolves; the receiving resolver answers null for that and renders
                 nothing, so no claim is made on the first paint. */
              homes={homes}
              suppressOpen={ctl.suppressOpen}
              onOpenRow={ctl.onOpenRow}
              openRequestId={openWaiting ?? (currentKey === null ? openRequestId : undefined)}
              inTodo
              arrange={ctl.arrange}
              beside
              {...(currentKey === null ? {} : { homeKey: currentKey, onStale: () => openHome(null) })}
            />
          ),
        }}
      />
    );
  } else if (tab === 'bookings') {
    /* ⭐ board 2812 M4 (step 4, step 0 (f)): the shared list, never the old table. All
       homes' Bookings is Who: All homes; a home's is that home (no Who row), with the
       When row and Find; 2814 M5's "Add a phoned-in trip" sits above it, scoped to the
       home on a home's page. */
    tabContent = (
      <HomesBookingsTab
        key={`bookings-${keyOf}`}
        registry={registry}
        onOpenBooking={(bookingId, at) => onOpenBooking(bookingId, 'bookings', at)}
        landingWhen={bookingsWhenAtEntry}
        onLanded={() => setBookingsWhenAtEntry(undefined)}
        homeKey={currentKey}
        fixedAccountId={currentAccount?.accountId}
        onStale={() => openHome(null)}
        /* ⭐ board 2812 M4 fix round (drive finding D1): Tomorrow's waiting request is a
           Need you now row, so it opens on this To do with its card open, as Bookings'
           own waiting row does; before, the row was pressable and did nothing. */
        onOpenWaiting={(requestId) => {
          setOpenWaiting(requestId);
          setDetailsOpen(false);
          setTab('todo');
        }}
      />
    );
  } else if (tab === 'prices') {
    tabContent = currentKey === null
      ? (
        <HomesPriceCounts
          groups={homeGroups}
          accounts={accounts}
          book={book}
          onOpen={(key) => {
            openHome(key);
            setTab('prices');
          }}
        />
      )
      : <HomePrices key={`prices-${keyOf}`} account={pageAccount} book={book} onBookChanged={reloadBook} />;
  } else {
    /* TAB 4 - the invoices and what every home owes; on a home's page, its own (the
       roll-up's account filter). ⚠️ It is handed the accounts THIS screen already read
       rather than fetching them again: a warning row carries an account id and no
       name, deliberately, and this is where the name is joined back on. */
    tabContent = currentKey === null
      ? <AccountInvoices key="invoices-all" accounts={accounts} />
      : pageAccount === undefined
        ? <p class="helper-note" role="status">{HOME_PAGE_WORDS.accountLoading}</p>
        : pageAccount === null
        ? <Banner kind="error">{HOME_PAGE_WORDS.accountUnread}</Banner>
        : <AccountInvoices key={`invoices-${keyOf}`} accounts={accounts} accountId={pageAccount.accountId} onStale={() => openHome(null)} />;
  }

  return (
    // The pane wrapper every promoted rail destination needs: a screen that
    // renders straight into `.content` (which declares no padding) sits flush
    // against the navy rail.
    <div class="screen-pane screen-pane--wide">
      <div class="homes-layout">
      {column}
      <div class="homes-main">
      {!setupOpen && (
        <>
          {currentKey === null ? (
            <div class="book-list-head">
              <h1 class="screen-title" tabIndex={-1}>{HOME_PAGE_WORDS.allHomes}</h1>
            </div>
          ) : (
            /* ⭐ board 2812 M4 fix round (C2): NEVER A HEADING-LESS HOME PAGE. Before the
               column's read lands, or when it fails, the title is the last name a read
               gave this key, else the account's name, else "This home"; each tab's own
               read answers a 404 by falling back to All homes. */
            <HomeHeading
              name={currentGroup?.name ?? knownNames.current.get(currentKey) ?? currentAccount?.name ?? HOME_PAGE_WORDS.thisHome}
              subtitle={currentGroup === null ? null : homeSubtitle(currentGroup)}
              detailsOpen={detailsOpen}
              onDetails={() => {
                setNotice(null);
                setDetailsOpen(!detailsOpen);
              }}
            />
          )}
          {/* ⭐⭐ board 2812 M4 (R-2812-e) — TWO LEVELS, ONE TAB SET: All homes and a home
              both show exactly To do, Bookings, Prices and Invoices, on the shared
              `TabStrip` (`aria-pressed` toggle buttons, never `role="tab"`). ⛔ THE
              WAITING COUNT RIDES ON THE `To do` BUTTON, from the same store value the
              rail badge renders (a home's from its group's count). */}
          <TabStrip
            label="Which part of nursing homes to show"
            tabs={TAB_ORDER.map((t) => ({
              view: t,
              label: t !== 'todo' ? TAB_LABELS[t] : countPending ? TAB_LABELS.todo : requestsTabLabel(toDoCount),
            }))}
            /* ⭐ board 2812 M4 fix round (L2): while Details is open NO tab is current (the
               page shows the home's settings, not a tab), and Details says it is open. */
            current={detailsOpen && currentKey !== null ? null : tab}
            onSelect={(v) => {
              tabChosen.current = true;
              setNotice(null);
              setDetailsOpen(false);
              setOpenWaiting(undefined);
              setTab(v);
            }}
          />
          {noticeBanner}
          {detailsOpen && currentKey !== null ? (
            <HomeDetails
              account={pageAccount}
              book={book}
              /* ⛔ "Nobody can sign in" only when the portal read WORKED and found no
                 home for this account: loading and a failed read say so instead. */
              portalBlocks={state.kind === 'loading'
                ? <p class="helper-note" role="status">Getting the homes and their nurses…</p>
                : state.kind === 'error'
                  ? <Banner kind="error">Could not read the homes and nurses from the portal.</Banner>
                  : linkedPortalHomes.length === 0 ? null : <>{linkedPortalHomes.map((h, i) => rosterBlock(h, i))}</>}
              onBookChanged={onDetailsSaved}
            />
          ) : tabContent}
        </>
      )}

      {/* ⭐⭐ board 2812 M4 — SET UP HOMES AND PLACES: today's Homes tab content (the
          roster, then the account book's Accounts, Destinations and Agreed prices),
          reached from the column's foot, no longer a tab. ⛔ CONDITIONALLY RENDERED,
          never `hidden`: a `hidden` attribute keeps the nodes in the DOM, where
          `getByText` still finds them, so every "this is not on the other view"
          assertion would be vacuous. */}
      {setupOpen && (
      <>
      <div class="book-list-head">
        <h1 class="screen-title" tabIndex={-1}>{HOME_PAGE_WORDS.setUp}</h1>
      </div>
      {noticeBanner}
      {/* ⭐⭐ T1749 M7 wave-3 (R5, the driver's R12 decision) — ONE SECTION STRIP
          AT THE TOP OF THE PANE, one section at a time, the roster FIRST.
          R5 says the Homes tab IS the account book, homes included. The first cut
          appended the book under the roster and the measurement is why that is
          not enough: the roster ran to y ~4470 and the book's only primary to
          y 7045, so the shelf's link-through landed on a screen whose accounts
          were ten viewports down with nothing at the top saying they were there.
          §8: scroll is for reading a list, never for finding the primary.
          ⛔ IT IS A DECLARED SECOND SIZE OF THE SHARED STRIP RULE
          (`.tab-strip--section`), not a second strip design: smaller labels on the
          same band, so a reader can tell a SECTION strip from the screen's TAB
          strip at a glance. It declares no box property, so it mints no tuple.
          ⭐ board 2812 M5 (fix round 1, U5): Set up homes and places is its own page
          now (board 2812 M4), with no screen tab strip above it to be told apart from,
          so it wears the SAME size as every other tab bar (`.tab-strip`, the shared
          TabStrip's rule); `.tab-strip--section` stays the portal Staff screen's. */}
      <div
        class="tab-strip"
        role="group"
        aria-label="Which part of the homes and accounts to show"
      >
        {SECTION_ORDER.map((id) => (
          <Button
            key={id}
            variant="tab"
            ariaPressed={id === section}
            onClick={() => setSection(id)}
          >
            {SECTION_LABELS[id]}
          </Button>
        ))}
      </div>

      {section === 'homes' && (
      <>
      <div class="op-review">
        <h2 class="screen-sub">Who can sign in for the homes</h2>

        {state.kind === 'loading' && (
          <p class="helper-note" role="status">Getting the homes and their nurses…</p>
        )}

        {state.kind === 'error' && (
          <Banner kind="error">
            Could not read the homes and nurses from the portal. The requests tab is
            unaffected.{' '}
            <button type="button" class="btn btn--quiet btn--inline" onClick={load}>Try again</button>
          </Banner>
        )}

        {state.kind === 'ready' && (
          <>
            {/* ⛔ T1749 M7 rework 1 — THE STANDING PARAGRAPH IS GONE, at Alex's
                ask. It sat above the whole roster and explained a control the
                reader had not reached yet; §3.2's rule is that a screen says a
                thing where the reader acts on it, not at the top of a list.
                ⭐ AND ITS ONE FACT NOW HAS A HOME (T1749 M7 rework 1): the fold
                asked for "Recorded against your name." as a quiet line at the
                nurse-removal CONFIRM step, and rework 1 built that step —
                `Remove access` no longer taps straight into `run()`; it arms
                `removingNurse` and swaps in `ConfirmInPlace` (below), which
                carries the line as a `field-hint`. Putting it on every row would
                repeat it once per nurse, which is the noise the paragraph was
                removed for; it lives with the confirm, where she acts on it. */}
            {/* ⭐ T1749 M7 rework 1 — the library's `EmptyState` (§7.1). */}
            {homes.length === 0 && (
              <EmptyState headline="No homes are set up in the portal yet." />
            )}

            {homes.map((home, homeIndex) => rosterBlock(home, homeIndex))}

            {/* ⭐ THE BOOTSTRAP REGION — what makes the FIRST nurse at a home
                reachable, and the reason variant C needs no picker. */}
            <div class="op-review-home">
              <h3 class="screen-sub screen-sub--item">Not in the portal yet</h3>
              {accounts === null && (
                <p class="helper-note">
                  Could not read the customer accounts, so this list may be incomplete.
                </p>
              )}
              {accounts !== null && unlinkedAccounts.length === 0 && (
                <p class="helper-note">Every customer account is already in the portal.</p>
              )}
              {unlinkedAccounts.length > 0 && (
                <>
                  <p class="helper-note">
                    These have prices on the kiosk, but nobody at them can sign in to the
                    portal. If the home is already listed above, attach the account to it.
                    Only create a new home if it is genuinely not there yet.
                  </p>
                  {/* ⚠️ `op-list--accounts` ADDED AT T1749 M9 (DS-24), AND IT
                      PRESERVES THIS LIST RATHER THAN RESTYLING IT. These rows sat
                      inside `.op-review-home`, so they were picking up the PRICE
                      BOOK's four-track grid (`app.css`'s `.op-review-home .op-row`)
                      as collateral — a rule written for a screen this one has
                      nothing to do with. DS-24 turned the price book into a real
                      `Table` and retired that rule, which would have dropped this
                      list back to a left-packed flex. `.op-list--accounts` is the
                      byte-identical grid, named after what these rows ARE (customer
                      accounts), so the pixels here do not move. */}
                  <ul class="op-list op-list--accounts" role="list">
                    {unlinkedAccounts.map((a, accountIndex) => (
                      <li key={a.accountId} class="op-row">
                        <span class="op-name">{a.name}</span>
                        <span class="op-login">no portal home</span>
                        <span class="op-decision">
                          {/* ⭐ T1146 M2 — N7's FIX, AND IT IS AN AFFORDANCE FIX.
                              `setHomeAccountLink` already re-links and unlinks and
                              calls itself "THE CORRECTION PATH"; the defect was that
                              this region offered only the MINT. After an unlink the
                              freed account lands here, and minting a second home
                              collides on v7's partial unique index — with no DELETE
                              grant on portal.homes, there is no way back.
                              ⇒ attaching to an existing home is now the PRIMARY act
                              and minting is secondary and explicit about what it does.
                              ⚠️ Offered only when there IS an unattached home; a
                              picker with nothing in it is a dead control.

                              ⚠️ THE INTERIM, STATED RATHER THAN GLOSSED: this
                              attach posts to the same `setHomeAccountLink` that
                              still carries `void actor;` (board #1104), so the
                              act is UNATTRIBUTED until T1146 M5 lands
                              `home_access_events`. That is acceptable only
                              because the on-screen claim under "Recent changes"
                              — "Bringing a home in, or changing which account it
                              bills to, is not recorded here yet" — remains
                              TRUE in the interim. ⛔ M5 retires that claim and
                              must land the writer in the same change; do not
                              retire the copy before the events exist. */}
                          {/* ⛔ `.op-add`, NOT this row's `.op-decision`. A ux
                              review measured the difference: `.op-decision` is
                              `inline-flex … margin-left:auto` (app.css:1774), a
                              non-wrapping row authored for one or two short
                              buttons, and an unconstrained <select> sizes to its
                              LONGEST <option> — i.e. the longest home name — so a
                              label + select + two account-named buttons rendered
                              as one unbroken horizontal strip. `.op-add`
                              (app.css:1620-1621) is this file's own proven shape
                              for exactly this composition: `max-width: measure`
                              with `width:100%` on the select, stacking
                              label → select → button. It is used 250 lines above
                              for the per-home "Customer account for X" picker.
                              ⇒ reused rather than re-solved.

                              simplified: no click-to-reveal on this picker, so
                              each unlinked row shows label + select + 2 buttons
                              at rest. The "Add a nurse at X" flow above is the
                              reveal idiom if it is ever wanted. Upgrade trigger =
                              more than ~3 accounts sitting unlinked at once, or
                              Helen calling this region busy. Production holds
                              ZERO unlinked accounts today (both are linked), so
                              the clutter is currently hypothetical and a reveal
                              control would add a tap to the corrective path this
                              milestone exists to shorten. */}
                          {attachableHomes.length > 0 && (
                            <div class="op-add">
                              {/* ⭐ T1749 M7 rework 1 — `FormField` with `opaque`:
                                  the label names the ACCOUNT, and a label is in
                                  the recorder's interactive selector. */}
                              <FormField
                                id={`attach-${String(accountIndex)}`}
                                label={`Existing home for ${a.name}`}
                                kind="select"
                                opaque
                                disabled={busy}
                                value={attachChoice[a.accountId] ?? ''}
                                /* ⚠️ THE DISAMBIGUATION SURVIVES THE ADOPTION, and it
                                   is a measured fix rather than a nicety: two
                                   attachable homes sharing a name were
                                   indistinguishable in this list (1 distinct
                                   visible label for 2 different ids), and picking
                                   the wrong one is an UNATTRIBUTED act (`void
                                   actor;`, board #1104) whose only correction is
                                   an unlink. */
                                options={[
                                  { value: '', label: 'Choose a home…' },
                                  ...attachableHomes.map((h) => ({
                                    value: String(h.home_id),
                                    label: ambiguousHomeNames.has(h.name) ? `${h.name}, ${h.town}` : h.name,
                                  })),
                                ]}
                                onInput={(v: string) =>
                                  setAttachChoice((prev) => ({ ...prev, [a.accountId]: v }))
                                }
                              />
                              <button
                                type="button"
                                class="btn btn--secondary btn--inline"
                                disabled={busy || (attachChoice[a.accountId] ?? '') === ''}
                                data-fb-opaque /* account name in the label */
                                onClick={() => {
                                  const raw = attachChoice[a.accountId] ?? '';
                                  if (raw === '') return;
                                  const homeId = Number(raw);
                                  const home = homes.find((h) => h.home_id === homeId);
                                  void run(
                                    () => linkPortalHome(homeId, a.accountId),
                                    `${a.name} is attached to ${home?.name ?? 'that home'}.`,
                                  );
                                }}
                              >
                                Attach {a.name} to that home
                              </button>
                            </div>
                          )}
                          <button
                            type="button"
                            class="btn btn--quiet btn--inline"
                            disabled={busy}
                            data-fb-opaque /* account name in the label */
                            onClick={() =>
                              void run(
                                () => createPortalHome(a.accountId),
                                `${a.name} is in the portal. Add its first nurse below.`,
                              )
                            }
                          >
                            Create a new home for {a.name}
                          </button>
                        </span>
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </div>

            <div class="op-cred-actions op-review-export">
              <button type="button" class="btn btn--quiet btn--inline" onClick={() => void onCopyCsv()}>
                Copy the sheet
              </button>
              <button type="button" class="btn btn--quiet btn--inline" onClick={downloadCsv}>
                Download the sheet
              </button>
              {csvCopied === buildCsv() && (
                <span class="op-login">Copied. Paste it into an email to the home.</span>
              )}
            </div>
          </>
        )}
      </div>

      {/* Still on the HOMES tab — who did what. M3 built the table; this is its
          reader.
          ⛔ NOT DECORATION: the privilege model is flat, every signed-in
          operator can remove any nurse's access, and this list is the surface on
          which that becomes visible to the people it affects.
          ⚠️ IT STAYS WITH THE ROSTER rather than becoming a fourth tab: it is the
          audit trail OF the roster's controls, and a trail one mode away from the
          act it records is a trail nobody reads. */}
      <div class="op-activity">
        <h2 class="screen-sub">Recent changes</h2>
        {/* ⛔ SAY WHAT THIS LIST DOES *NOT* HOLD (board 971). It records nurse
            access — added, removed, restored — and nothing else, because
            `ops-repo.ts`'s two home writers carry `void actor;` and migration
            v8's CHECK enumerates nurse verbs only, so a home act has nowhere
            honest to sit in the table. That gap was accepted deliberately, at
            Captain's option, with the obligation that no surface assert an
            attribution that does not exist — and M5 is what puts the
            bring-a-home-in act (labelled "Create a new home for X" since
            T1146 M2, which also added the "Attach X to that home" picker
            beside it) and the account link under a heading a reader will
            take as general-purpose. This is the fourth site where the claim is scoped;
            the other three are the seam client's paragraph and both writers. */}
        <p class="helper-note">
          Nurse access only. Bringing a home in, or changing which account it bills to, is
          not recorded here yet.
        </p>
        {events === undefined && <p class="helper-note" role="status">Getting the recent changes…</p>}
        {events === null && (
          <p class="helper-note">Could not read the recent changes from the portal.</p>
        )}
        {events !== null && events !== undefined && events.length === 0 && (
          <p class="helper-note">Nothing has been changed here yet.</p>
        )}
        {events !== null && events !== undefined && events.length > 0 && (
          <ul class="op-list" role="list">
            {events.map((e) => (
              <li key={e.event_id} class="op-row">
                <span class="op-name">
                  {e.actor} {eventPhrase(e.event)} {e.nurse_email}
                </span>
                <span class="op-login">{e.created_at === null ? '' : shortDateTime(e.created_at)}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
      </>
      )}

      {/* ⭐⭐ T1749 M7 (R5, C7) — THE CUSTOMER ACCOUNT BOOK, UNDER THE HOMES TAB.
          R5, in the sitting's own words: "Accounts live under the Nursing homes
          tab. Its Homes tab (shipped by #1746) IS the account book: setting up
          accounts, homes, their destinations and agreed prices." This is the
          screen where a home is linked to an account, so the account it is linked
          TO belongs on it; before this, linking a home here and then pricing that
          account meant leaving for Price lists and coming back.

          ⛔ A NESTED STRIP, NOT FOUR MORE TOP-LEVEL TABS. The top strip is the
          four parts of nursing homes and stays exactly four (an absence pin says
          so); this is a strip INSIDE one of them, which is the shape the account
          book already had as a sub-view and is what keeps the Helen-test ("too
          many buttons") answerable on a screen that now holds two strips.
          ⛔ NO `onBack`: the book renders its strip and its lists alone here. A
          Back control would be a second one on a rail destination that already
          has App's, and its `<h1>` would be this screen's second top-level
          heading. See `AccountBook.tsx`'s prop for the reasoning.
          ⚠️ `onChanged` REFETCHES THE BOOK ALONE, not the roster: a price written
          here changes nothing about who can sign in, and reloading the roster
          would blank a list the operator is reading for no reason. */}
      {section !== 'homes' && (
        <AccountBook
          accounts={book.accounts}
          destinations={book.destinations}
          billingParties={book.billingParties}
          truncated={book.truncated}
          failed={book.failed}
          loaded={book.loaded}
          /* ⭐ The strip above IS the book's navigation now, so the book renders
             the one section the strip chose rather than a second strip of its
             own (its `Homes` segment is the roster, which is not the book's). */
          initialTab={BOOK_SECTIONS[section] ?? 'accounts'}
          hideStrip
          onChanged={reloadBook}
        />
      )}
      </>
      )}
      </div>
      </div>
    </div>
  );
}

/**
 * ⭐⭐ board 2812 M4 fix round (B1, the output gate's BLOCK) — THE BOOKINGS TAB OWNS ITS
 * FIND TERM. The term used to live on the screen while the box (`FindField`) kept its
 * own text, and this tab's content remounts when Helen leaves the tab or switches home:
 * the box came back EMPTY while the list kept filtering by the old term, a hidden
 * filter on the screen that lists rides. Held here, inside the keyed tab, the term and
 * its box mount and unmount together, so they always agree: leaving the tab, opening
 * Details or switching home starts both afresh, and the list's next read is the plain
 * filtered read with no term.
 * ⭐ board 2812 M4 fix round (L4): no `.op-review` wrapper. Its top margin and rule drew
 * a 48px dead band and a second hairline between the tab strip and "Add a phoned-in
 * trip"; the strip is the only seam this tab needs.
 * 2814 M5's "Add a phoned-in trip" sits above the shared list, scoped to the home on a
 * home's page (no Who row there; the When row and Find kept).
 */
function HomesBookingsTab({
  registry,
  onOpenBooking,
  landingWhen,
  onLanded,
  homeKey,
  fixedAccountId,
  onStale,
  onOpenWaiting,
}: {
  registry: Registry;
  /** ⭐ board 2812 M5 (fix round 1, W2): with the When the row was found under. */
  onOpenBooking: (bookingId: number, at?: BookingsLanding) => void;
  /** ⭐ (W2) the When to open on (Back from a booking), spent at mount (`onLanded`). */
  landingWhen?: BookingsWhen;
  onLanded?: () => void;
  /** The home's group key, or null for All homes. */
  homeKey: string | null;
  /** The home's account, when its page is showing and the accounts read worked. */
  fixedAccountId: number | undefined;
  onStale: () => void;
  /** Tomorrow's waiting request row: open it on this area's To do. */
  onOpenWaiting: (requestId: number) => void;
}) {
  const [find, setFind] = useState('');
  const [refresh, setRefresh] = useState(0);
  // ⭐ (W2) read once: the list's own useState keeps it; the host forgets it after.
  const [landing] = useState<BookingsLanding | undefined>(
    landingWhen === undefined ? undefined : { who: homeKey === null ? 'homes' : { home: homeKey }, when: landingWhen },
  );
  useEffect(() => {
    if (landingWhen !== undefined) onLanded?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <>
      <PhoneInTrip
        onOpenBooking={onOpenBooking}
        onBooked={() => setRefresh((n) => n + 1)}
        {...(fixedAccountId === undefined ? {} : { fixedAccountId })}
      />
      <div class="book-list-head">
        <FindField onFind={setFind} />
      </div>
      <BookingsList
        registry={registry}
        onOpen={onOpenBooking}
        idPrefix="homes-bookings"
        fixedWho={homeKey === null ? 'homes' : { home: homeKey }}
        {...(landing === undefined ? {} : { landing })}
        find={find}
        beside
        dropHome={homeKey !== null}
        refreshKey={refresh}
        onOpenWaiting={onOpenWaiting}
        {...(homeKey === null ? {} : { onStale })}
      />
    </>
  );
}

/**
 * The `Requests` tab's label, carrying the waiting count.
 *
 * ⛔ THREE STATES, NOT TWO, AND THE THIRD IS THE POINT. `null` means the badge's
 * own read FAILED; saying "Requests" there would assert that nothing is waiting
 * on the exact screen the operator came to check, which is the "never a silent
 * zero" rule the three badge counters already keep. Collapsing it into the zero
 * arm is the mutation this discharges.
 */
/**
 * ⭐⭐ T1749 M7 wave-3 — `Requests · 25`, NOT `Requests (25 waiting)`, AND THE
 * REASON IS A MEASUREMENT RATHER THAN A PREFERENCE. Measured at 1024x700 in the
 * live drive: the parenthesised form is the widest label in the strip and wrapped
 * it onto a second row, taking the strip from about 48px to 75px and pushing the
 * open row's gold primary further below the ~700 plane (R11, impeccable P1-2).
 * The middot is this tool's own separator (the copy gate bans both dashes and
 * `bookingRef`'s control already uses it), and the count is the same number the
 * rail badge renders.
 * ⚠️ THE THREE STATES ARE UNCHANGED: no count when nothing waits, the count when
 * something does, and an honest sentence when the read failed. Only the middle
 * one got shorter — the failure state stays a sentence, because it is rare and
 * it has to be readable rather than short.
 */
/* ⭐ Board 2788 M6 (step 1), re-pointed at board 2814 M2 (DD-11): the number is the
   Needs you now length (`needsHelenNow` in `lib/actions.ts`), the same value the
   rail badge renders and the Requests screen lists; the label stays plain,
   `To do · N` (board 2812 M3 renamed the tab), whatever is being waited on. */
function requestsTabLabel(waiting: number | null): string {
  if (waiting === null) return `${TAB_LABELS.todo} (count unavailable)`;
  if (waiting === 0) return TAB_LABELS.todo;
  return `${TAB_LABELS.todo} · ${String(waiting)}`;
}
